/**
 * content-builder-neo-ext.js — theme additions to the AICO Neo renderer.
 *
 * PORTED from the FC Lausanne-Sport theme (same filename there, branch
 * neo-pages-render / p31, 2026-08-25 state) with the Lausanne-document-specific
 * hydrators removed (their §16 product-rail chevrons, §17 home marquee) and the
 * tenant constant + form copy adapted to FC Zürich. Keep it diffable against
 * the Lausanne file — internal names (`ls-neo-loading`, `data-ls-slot`,
 * `ls:theme-slots-placed`, `lsT`) are deliberately kept identical.
 *
 * The engine (`assets/content-builder-neo.js` → window.NewsNeo, itself the
 * theme's news-neo.js plus a bootstrap) stays a pure element renderer, so
 * everything page-specific lives here. Loaded AFTER the engine (both `defer`,
 * so document order is execution order). Load this pair ONLY on Neo page
 * templates (snippets/aico-neo-content.liquid) — news pages load news-neo.js
 * without this file, and this file replaces Neo.render with a section-wrapping
 * version news.js must not see.
 *
 * WHY THIS EXISTS (history from the Lausanne pilot): /pages/club-des-130
 * rendered a single image. Its Neo document uses {html, container, heading,
 * text, columns, image, linked-form} and the engine ships no `container` /
 * `linked-form` renderer. An unknown type returns '' — INCLUDING its children —
 * and 10 of the 17 elements on that page are nested inside 3 containers, so
 * nearly everything vanished.
 *
 * What it adds:
 *   1. `container`      — generic wrapper div: element styles inline + children
 *                         rendered recursively (matches aiconeo's ElementRenderer
 *                         `case 'container'`: a plain styled div whose children
 *                         are direct siblings, no extra wrapper).
 *   2. unknown-with-children degradation — any unregistered type that carries a
 *                         `children` array is rewritten to `container` before
 *                         the engine sees it, so a wrapper-ish unknown degrades
 *                         to its content instead of deleting a subtree. Warns once
 *                         per type.
 *   3. nested `columns`  — the engine only handles `columns` at section level
 *                         (renderSection); a `columns` INSIDE a container would
 *                         otherwise render nothing. Reimplemented here.
 *   4. `linked-form`     — a real, working aico form (see the contract in
 *                         docs/aico-neo-pages.md).
 *   5. `styles.mobile`   — the engine inlines `styles.desktop` only. For the
 *                         types rendered here, the mobile override is emitted as
 *                         a scoped @media rule so containers don't keep their
 *                         160px desktop padding on a phone.
 *   6. SECTION pass      — the engine has no section wrapper at all, so section
 *                         backgrounds/padding, `layout.mode` (full/contained/
 *                         custom) and the page `settings.backgroundColor` were
 *                         all dropped. Re-rendered per section and wrapped here,
 *                         mirroring aiconeo's PublicPageRenderer. Also honours
 *                         `visibleFrom` / `visibleUntil`.
 *                         NOT implemented: `dividerTop` / `dividerBottom`
 *                         (clip-path polygons from renderers/SectionDivider) —
 *                         absent from the pilot document and not cheap.
 *   7. className/customId — the engine drops both; aiconeo stamps them on every
 *                         element. Without them /pages/ls-is-more lost its whole
 *                         @container-query layout system and its anchor targets.
 *   8. `button`          — the engine registers none, and unknown leaves have no
 *                         children to fall back to, so /pages/kids rendered zero
 *                         CTAs. Mirrors ElementRenderer's button, defaults and
 *                         two-node shape included.
 *
 *   9. section-level `columns` — the one element the registry decorator cannot
 *                         reach (renderSection intercepts it before dispatch);
 *                         its styles/className/customId are patched into the
 *                         engine's output.
 *  10. loud failure     — an unrenderable LEAF now emits an HTML comment marker
 *                         in place, and each render logs ONE aggregated warning
 *                         listing every missing type with counts.
 *  11. Tier-1 elements  — spacer, badge, cta, hero, anchor-nav, marquee,
 *                         partner-logos, file-list, social-embed, multicolumn,
 *                         features, testimonial, faq, accordion,
 *                         feature-accordion.
 *  12. size-container guard — an authored `container-type: inline-size` inside an
 *                         `align-items:flex-start` container collapses to width 0
 *                         and renders prose one word per line. Measured at
 *                         hydrate and given an inline size. (Its sibling fix, the
 *                         `.content-row` track lists, is CSS —
 *                         assets/content-builder-neo.css.)
 *  13. `image` effects  — the engine forwards only src/alt/caption + the desktop
 *                         style bag, so aiconeo's shape (clip-path), crop/fit and
 *                         overlay scrim were all dropped. /pages/hospitalites
 *                         authors a diagonal shape on its match photo and the
 *                         storefront drew a plain rectangle.
 *  14. empty-column collapse — a column whose only element rendered to '' (an
 *                         `image` with no `content.src`, a `youtube`/`video`
 *                         with no id, an unrenderable leaf) still gets its
 *                         `.content-column` wrapper and still claims its half of
 *                         the row, so /pages/enzonet-lounge drew an empty dark
 *                         box beside its text. Detected and collapsed at hydrate.
 *  15. theme slots      — a generic mount point for Shopify sections that cannot
 *                         be authored in the builder at all (the jersey
 *                         configurator resolves products/variants/metafields in
 *                         Liquid). The author drops an html element holding
 *                         `<div data-ls-slot="name">` where the section should
 *                         appear; the theme stamps `data-ls-slot-source="name"`
 *                         on the section root, and hydrate MOVES the
 *                         server-rendered node into the slot. No slot → the
 *                         section stays where the template put it, visible.
 *  19. locale resolution — the >64KB URL fallback fetches the RAW document,
 *                         whose per-language texts sit unapplied in
 *                         `localeOverrides`. `Neo.resolveLocale(doc, lang,
 *                         default)` ports aico's NeoLocaleResolver so the FR/EN
 *                         switcher works on oversized pages too (the <64KB
 *                         metafield keys arrive already resolved).
 *
 * (Sections 16/17 of the Lausanne file — chevrons for its shop page's authored
 * product rails and its home marquee fit — target class names authored in
 * specific Lausanne documents and are NOT ported. Numbering of the remaining
 * sections is kept so the two files diff cleanly.)
 *
 * Verified in the Lausanne theme against its landing pages 2 (club-des-130),
 * 5 (hospitalites), 12 (kids) and 14 (ls-is-more); not yet against a live
 * FC Zürich document.
 *
 * The small style/escape helpers below replicate the engine's own — they are
 * IIFE-scoped there and intentionally not exported.
 */
(function (window, document) {
  'use strict';

  var Neo = window.NewsNeo;
  if (!Neo || typeof Neo.registerType !== 'function') {
    if (window.console && console.warn) {
      console.warn('content-builder-neo-ext: NewsNeo engine missing — load content-builder-neo.js first');
    }
    return;
  }

  /**
   * Legacy public submit endpoint, used ONLY when the authored element carries
   * no `submitEndpoint`. aiconeo bakes an absolute `submitEndpoint` into every
   * linked-form element it saves (LinkedFormRenderer.handleFormSelect →
   * buildFormSubmissionEndpoint), so this should never be needed.
   * FLAG: this one constant is the only tenant-specific value in the file.
   * Everything else (including the form-definition read URL) is derived from
   * the authored endpoint. It points at the Bunny pull zone for aico tenant
   * 19838 (FC Zürich) — this theme routes every aico call through the edge so
   * no aico host/token appears in theme code (2026-08-26 decision). NOTE: both
   * form endpoints are public/unauthenticated at origin, but check the zone
   * forwards `/api/v1/news/form-submissions` (POST) and
   * `/api/aiconeo/public/aico-forms/{id}` before shipping a page with a form.
   */
  var FALLBACK_SUBMIT_ENDPOINT = 'https://fczurich-data.b-cdn.net/api/v1/news/form-submissions';

  // ============================================================
  // Helpers (mirroring content-builder-neo.js)
  // ============================================================

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function camelToKebab(s) {
    return s.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  // Prefix bare domains with https:// so they don't become relative paths;
  // drop javascript: URLs. Leaves #anchors and absolute/root paths untouched.
  function normalizeUrl(url) {
    if (!url) return '';
    var s = String(url).trim();
    if (!s) return '';
    if (/^javascript:/i.test(s)) return '';
    if (/^([a-z][a-z0-9+.\-]*:|\/\/|\/|#)/i.test(s)) return s;
    if (/^[^\s\/?]+\.[a-z]{2,}/i.test(s)) return 'https://' + s;
    return s;
  }

  // Expand padding/margin shorthand into longhands when both are present.
  function normalizeShorthands(styleObj) {
    var out = {};
    for (var k in styleObj) {
      if (Object.prototype.hasOwnProperty.call(styleObj, k)) out[k] = styleObj[k];
    }
    var sides = ['Top', 'Right', 'Bottom', 'Left'];
    ['padding', 'margin'].forEach(function (base) {
      var hasIndividual = sides.some(function (s) { return (base + s) in out; });
      if (!hasIndividual || !(base in out)) return;
      var parts = String(out[base]).trim().split(/\s+/);
      var top = parts[0];
      var right = parts[1] !== undefined ? parts[1] : top;
      var bottom = parts[2] !== undefined ? parts[2] : top;
      var left = parts[3] !== undefined ? parts[3] : right;
      var fill = { Top: top, Right: right, Bottom: bottom, Left: left };
      sides.forEach(function (s) { if (!(base + s in out)) out[base + s] = fill[s]; });
      delete out[base];
    });
    return out;
  }

  function stylesToInline(styleObj) {
    if (!styleObj || typeof styleObj !== 'object' || Array.isArray(styleObj)) return '';
    var normalized = normalizeShorthands(styleObj);
    var out = [];
    for (var key in normalized) {
      if (!Object.prototype.hasOwnProperty.call(normalized, key)) continue;
      var val = normalized[key];
      if (val === null || val === undefined || val === '') continue;
      out.push(camelToKebab(key) + ':' + val);
    }
    return out.join(';');
  }

  function pickStyles(node, breakpoint) {
    if (!node || !node.styles) return {};
    var s = node.styles[breakpoint || 'desktop'];
    if (!s || Array.isArray(s)) return {};
    return s;
  }

  function inlineStyleAttr(node) {
    var s = stylesToInline(pickStyles(node));
    return s ? ' style="' + escapeAttr(s) + '"' : '';
  }

  // Only [a-z0-9-] survives, so the id can never break out of the attribute
  // selector it is interpolated into.
  function safeId(id) {
    return String(id == null ? '' : id).toLowerCase().replace(/[^a-z0-9\-]/g, '');
  }

  /**
   * The engine inlines `styles.desktop` only. Emit `styles.mobile` as a scoped
   * @media block so the authored mobile override actually applies. `!important`
   * is required: it has to beat the inline desktop style on the same node.
   * Returns '' when there is no mobile override or no usable id.
   */
  function mobileStyleTag(node) {
    var mobile = pickStyles(node, 'mobile');
    var css = stylesToInline(mobile);
    var id = safeId(node && node.id);
    if (!css || !id) return '';
    var declarations = css.split(';').filter(Boolean).map(function (d) {
      return d + ' !important';
    }).join(';');
    return '<style>@media screen and (max-width: 749px){' +
      '[data-neo-el="' + id + '"]{' + declarations + '}}</style>';
  }

  function dataElAttr(node) {
    var id = safeId(node && node.id);
    return id ? ' data-neo-el="' + id + '"' : '';
  }

  // ============================================================
  // Child dispatch
  //
  // The engine's renderElement / renderColumnsRow are IIFE-scoped, so children
  // are dispatched here against the LIVE registry (Neo._renderers) — which
  // includes the types registered below, so containers nest recursively.
  // ============================================================

  function renderChild(el) {
    if (!el || typeof el !== 'object') return '';
    // Nested columns never enter the registry, so decorate them here instead.
    if (el.type === 'columns') return decorateElementHtml(el, renderNestedColumns(el));
    var fn = Neo._renderers[el.type];
    if (typeof fn !== 'function') {
      warnUnknownOnce(el.type);
      return renderChildren(el);
    }
    try {
      return fn(el);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: renderer for "' + el.type + '" threw', e);
      }
      return '';
    }
  }

  function renderChildren(el) {
    var children = (el && Array.isArray(el.children)) ? el.children
      : (el && Array.isArray(el.elements)) ? el.elements
        : [];
    var html = '';
    for (var i = 0; i < children.length; i++) html += renderChild(children[i]);
    return html;
  }

  var warnedTypes = {};
  function warnUnknownOnce(type) {
    var key = String(type);
    if (warnedTypes[key]) return;
    warnedTypes[key] = true;
    if (window.console && console.warn) {
      console.warn('content-builder-neo-ext: no renderer for Neo element type "' + key +
        '" — rendering its children only');
    }
  }

  // ============================================================
  // 1. container
  // ============================================================

  Neo.registerType('container', function (el) {
    return mobileStyleTag(el) +
      '<div class="content-type-neo-container"' + dataElAttr(el) + inlineStyleAttr(el) + '>' +
      renderChildren(el) +
      '</div>';
  });

  // ============================================================
  // 8. button
  //
  // The engine registers no `button` renderer at all, and the unknown-type
  // degradation above only rescues elements WITH children — so leaf buttons
  // rendered nothing. /pages/kids lost all five of its CTAs that way.
  //
  // Mirrors ElementRenderer's __InlineEditableButtonOriginal exactly, including
  // its defaults, which are NOT cosmetic here: that renderer overrides the
  // authored borderRadius/padding/border with shape/size/borderWidth defaults
  // (filled + rounded + md + borderWidth 2 → 8px radius, 12px 24px padding,
  // 2px solid #000). The kids document knows this and compensates in its own
  // CSS — it literally says so:
  //
  //   "the renderer forces a 2px solid black border + 8px radius on every
  //    button; the LS kit's CTAs are square with a 1px transparent border"
  //   "the className lands on the element's OWN div AND the renderer nests an
  //    <a> inside it … The visible border belongs to the <a>/<button> only"
  //
  // So the two-node shape (classNamed wrapper > styled <a>) and the defaults
  // both have to be reproduced, or the author's `.kid-btn a` / `.kid-btn--ghost a`
  // !important rules stop matching and the buttons break differently.
  //
  // Not ported: `buttonIcon` (needs the lucide set) and the smooth-scroll
  // handler for `#section-…` hrefs — a native anchor jump already works, since
  // the section pass emits `id="section-<id>"`.
  // ============================================================

  var BUTTON_SHAPE_RADIUS = { square: '0px', rounded: '8px', pill: '9999px' };
  var BUTTON_SIZE_STYLES = {
    sm: { padding: '8px 16px', fontSize: '13px' },
    md: { padding: '12px 24px', fontSize: '15px' },
    lg: { padding: '16px 32px', fontSize: '17px' },
    xl: { padding: '20px 40px', fontSize: '19px' }
  };
  var BUTTON_SHADOWS = [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.05)',
    '0 4px 6px -1px rgba(0,0,0,0.1)',
    '0 10px 15px -3px rgba(0,0,0,0.1)',
    '0 20px 25px -5px rgba(0,0,0,0.1)'
  ];
  var BUTTON_ALIGN_TO_JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

  Neo.registerType('button', function (el) {
    var content = el.content || {};
    var text = content.text != null ? String(content.text) : '';
    var url = normalizeUrl(content.url);
    var openInNewTab = !!content.openInNewTab;

    // textAlign moves to the wrapper as justifyContent; everything else stays
    // on the button node.
    var authored = pickStyles(el, 'desktop');
    var buttonStyles = {};
    for (var k in authored) {
      if (Object.prototype.hasOwnProperty.call(authored, k) && k !== 'textAlign') {
        buttonStyles[k] = authored[k];
      }
    }

    var variant = content.buttonVariant || 'filled';
    var shape = content.buttonShape || 'rounded';
    var size = content.buttonSize || 'md';
    var bgColor = content.buttonBgColor || '';
    var textColor = content.buttonTextColor || '';
    var borderColor = content.buttonBorderColor || '';
    var borderWidth = content.buttonBorderWidth == null ? 2 : content.buttonBorderWidth;
    var fullWidth = !!content.buttonFullWidth;
    var shadowLevel = content.buttonShadow == null ? 0 : content.buttonShadow;
    var sizeStyle = BUTTON_SIZE_STYLES[size] || BUTTON_SIZE_STYLES.md;

    var computed = {};
    for (var b in buttonStyles) {
      if (Object.prototype.hasOwnProperty.call(buttonStyles, b)) computed[b] = buttonStyles[b];
    }
    computed.borderRadius = BUTTON_SHAPE_RADIUS[shape] || '8px';
    computed.padding = sizeStyle.padding;
    computed.fontSize = buttonStyles.fontSize || sizeStyle.fontSize;
    computed.boxShadow = BUTTON_SHADOWS[shadowLevel] || 'none';
    computed.display = 'inline-flex';
    computed.alignItems = 'center';
    computed.gap = '8px';
    computed.textDecoration = 'none';
    computed.transition = 'all 0.2s ease';
    computed.fontWeight = buttonStyles.fontWeight || '600';
    computed.cursor = 'pointer';
    if (fullWidth) {
      computed.width = '100%';
      computed.justifyContent = 'center';
    }

    // A theme-token colour flips with light/dark mode and turns unreadable on a
    // solid button, so upstream ignores it there; an explicit buttonTextColor
    // still wins.
    var inheritedColor = (typeof buttonStyles.color === 'string' && buttonStyles.color.indexOf('var(--') !== -1)
      ? undefined
      : buttonStyles.color;

    if (variant === 'filled') {
      computed.backgroundColor = bgColor || buttonStyles.backgroundColor || '#000000';
      computed.color = textColor || inheritedColor || '#ffffff';
      computed.border = borderWidth > 0 ? borderWidth + 'px solid ' + (borderColor || '#000000') : 'none';
    } else if (variant === 'outline') {
      computed.backgroundColor = 'transparent';
      computed.color = textColor || borderColor || buttonStyles.color || '#000000';
      computed.border = (borderWidth > 0 ? borderWidth : 2) + 'px solid ' + (borderColor || textColor || '#000000');
    } else if (variant === 'ghost') {
      computed.backgroundColor = bgColor || 'rgba(0,0,0,0.05)';
      computed.color = textColor || inheritedColor || (bgColor ? '#ffffff' : '#000000');
      computed.border = borderWidth > 0 ? borderWidth + 'px solid ' + (borderColor || '#000000') : 'none';
    } else if (variant === 'link') {
      computed.backgroundColor = 'transparent';
      computed.color = textColor || buttonStyles.color || '#000000';
      computed.border = 'none';
      computed.padding = '0';
      computed.textDecoration = 'underline';
      computed.boxShadow = 'none';
    }

    var wrapperStyle = authored.textAlign
      ? 'display:flex;justify-content:' + (BUTTON_ALIGN_TO_JUSTIFY[authored.textAlign] || 'flex-start')
      : '';

    var anchorAttrs = '';
    if (url) anchorAttrs += ' href="' + escapeAttr(url) + '"';
    if (openInNewTab) anchorAttrs += ' target="_blank" rel="noopener noreferrer"';

    // data-neo-el marks the STYLED node, so the styles.mobile @media override
    // lands on the <a> rather than the wrapper.
    return mobileStyleTag(el) +
      '<div class="content-type-button content-type-neo-button"' +
      (wrapperStyle ? ' style="' + escapeAttr(wrapperStyle) + '"' : '') + '>' +
      '<a class="neo-button"' + anchorAttrs + dataElAttr(el) +
      ' style="' + escapeAttr(stylesToInline(computed)) + '">' +
      escapeHtml(text) +
      '</a></div>';
  });

  // ============================================================
  // 10. Tier-1 content elements
  //
  // aiconeo defines 58 element types; the engine renders 10 and the blocks
  // above add 4. These are the ones a human clicking through the aiconeo
  // palette on a club marketing page is most likely to reach for, all of them
  // pure markup from `content` with no runtime data dependency.
  //
  // FIDELITY NOTE (applies to every renderer below): aiconeo's markup is
  // Tailwind utility classes + Radix primitives + lucide icons, none of which
  // exist in this theme. Reproducing its class strings is impossible and
  // meaningless here. What IS mirrored, and what the harness checks, is:
  //   - the exact `content` field names, defaults and fallback chains,
  //   - the DOM shape (which node is the wrapper, what nests inside what),
  //   - which node carries the element's inline styles.
  // Visual styling is supplied by content-builder-neo.css using the theme's
  // own tokens. Deviations beyond that are called out per renderer.
  //
  // Lucide `icon` name fields (multicolumn, features) cannot be resolved
  // without the icon set: an `iconUrl` is rendered as an <img>, a bare `icon`
  // name renders the icon frame without a glyph.
  // ============================================================

  /** Item arrays are authored as `content.items`; tolerate a missing/!array value. */
  function itemsOf(content, key) {
    var v = content && content[key || 'items'];
    return Array.isArray(v) ? v : [];
  }

  /** `style="…"` from a plain object, or '' when empty. */
  function styleAttrOf(obj) {
    var css = stylesToInline(obj);
    return css ? ' style="' + escapeAttr(css) + '"' : '';
  }

  /** Shared opening tag for a Tier-1 wrapper: styles + data-neo-el. */
  function wrapperOpen(el, cls, extraStyles) {
    var styles = pickStyles(el, 'desktop');
    var merged = {};
    for (var k in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, k)) merged[k] = styles[k];
    }
    if (extraStyles) {
      for (var e in extraStyles) {
        if (Object.prototype.hasOwnProperty.call(extraStyles, e)) merged[e] = extraStyles[e];
      }
    }
    return '<div class="' + cls + '"' + dataElAttr(el) + styleAttrOf(merged) + '>';
  }

  // ---- spacer -------------------------------------------------
  // Upstream puts NO style on the wrapper and cssStyles + transparent
  // background on an inner div; height comes from styles.desktop (default 48px).
  Neo.registerType('spacer', function (el) {
    var styles = pickStyles(el, 'desktop');
    var merged = {};
    for (var k in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, k)) merged[k] = styles[k];
    }
    merged.background = 'transparent';
    return mobileStyleTag(el) +
      '<div class="content-type-neo-spacer">' +
      '<div' + dataElAttr(el) + styleAttrOf(merged) + '></div></div>';
  });

  // ---- badge --------------------------------------------------
  // content: text, badgeStyle (primary|secondary|outline), badgePadding (12),
  // badgeAlign (left|center|right). Upstream derives the pill padding from
  // badgePadding as `max(2, p*0.35)px  p px` and picks a font size by threshold.
  Neo.registerType('badge', function (el) {
    var c = el.content || {};
    var style = c.badgeStyle || 'primary';
    var padding = c.badgePadding == null ? 12 : Number(c.badgePadding);
    var align = c.badgeAlign || 'left';
    var justify = { left: 'flex-start', center: 'center', right: 'flex-end' }[align] || 'flex-start';
    var fontSize = padding <= 10 ? '1.2rem' : padding <= 18 ? '1.4rem' : '1.6rem';
    var pill = {
      padding: Math.max(2, Math.round(padding * 0.35)) + 'px ' + padding + 'px',
      fontSize: fontSize
    };
    return mobileStyleTag(el) +
      wrapperOpen(el, 'content-type-neo-badge', { display: 'flex', justifyContent: justify }) +
      '<span class="neo-badge neo-badge--' + escapeAttr(style) + '"' + styleAttrOf(pill) + '>' +
      escapeHtml(c.text || 'Badge') +
      '</span></div>';
  });

  // ---- cta ----------------------------------------------------
  // content: headline, description, buttonText, buttonUrl, ctaHeadlineColor,
  // ctaDescColor, and buttonBgColor/buttonTextColor with the legacy
  // ctaButtonBgColor/ctaButtonTextColor fallback upstream still honours.
  Neo.registerType('cta', function (el) {
    var c = el.content || {};
    var url = normalizeUrl(c.buttonUrl);
    var btnBg = c.buttonBgColor || c.ctaButtonBgColor || '';
    var btnFg = c.buttonTextColor || c.ctaButtonTextColor || '';
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-cta');
    if (c.headline) {
      html += '<h2 class="neo-cta__headline"' + styleAttrOf({ color: c.ctaHeadlineColor || undefined }) + '>' +
        escapeHtml(c.headline) + '</h2>';
    }
    if (c.description) {
      html += '<p class="neo-cta__description"' + styleAttrOf({ color: c.ctaDescColor || undefined }) + '>' +
        escapeHtml(c.description) + '</p>';
    }
    if (c.buttonText) {
      html += '<a class="neo-cta__button"' + (url ? ' href="' + escapeAttr(url) + '"' : '') +
        styleAttrOf({ backgroundColor: btnBg || undefined, color: btnFg || undefined }) + '>' +
        escapeHtml(c.buttonText) + '</a>';
    }
    return html + '</div>';
  });

  // ---- hero ---------------------------------------------------
  // content: headline, subheadline, buttonText, buttonUrl, backgroundImage,
  // backgroundPosition (center), overlayOpacity (0), contentPosition (center),
  // heroTextAlign (center), contentMaxWidth, heroHeight.
  // Upstream maps contentPosition through a 9-cell justify/align grid onto the
  // wrapper, which also carries the background image.
  // Deviation: upstream's `sectionBlocks` ordering/spacing model is not ported —
  // blocks render in the fixed heading → subheadline → button order, which is
  // its default when `sectionBlocks` is absent.
  var HERO_POSITIONS = {
    'top-left': ['flex-start', 'flex-start'], 'top-center': ['flex-start', 'center'], 'top-right': ['flex-start', 'flex-end'],
    'center-left': ['center', 'flex-start'], 'center': ['center', 'center'], 'center-right': ['center', 'flex-end'],
    'bottom-left': ['flex-end', 'flex-start'], 'bottom-center': ['flex-end', 'center'], 'bottom-right': ['flex-end', 'flex-end']
  };

  Neo.registerType('hero', function (el) {
    var c = el.content || {};
    var pos = HERO_POSITIONS[c.contentPosition || 'center'] || HERO_POSITIONS.center;
    var bg = c.backgroundImage ? 'url(' + String(c.backgroundImage).replace(/[()"']/g, '') + ')' : undefined;
    var extra = {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: pos[0],
      alignItems: pos[1],
      width: '100%',
      backgroundImage: bg,
      backgroundSize: bg ? 'cover' : undefined,
      backgroundPosition: bg ? (c.backgroundPosition || 'center') : undefined,
      minHeight: c.heroHeight || undefined
    };
    var overlay = Number(c.overlayOpacity || 0);
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-hero', extra);
    if (overlay > 0) {
      html += '<div class="neo-hero__overlay"' +
        styleAttrOf({ backgroundColor: 'black', opacity: overlay }) + '></div>';
    }
    html += '<div class="neo-hero__content"' +
      styleAttrOf({ textAlign: c.heroTextAlign || 'center', maxWidth: c.contentMaxWidth || undefined }) + '>';
    if (c.headline) html += '<h1 class="neo-hero__headline">' + escapeHtml(c.headline) + '</h1>';
    if (c.subheadline) html += '<p class="neo-hero__subheadline">' + escapeHtml(c.subheadline) + '</p>';
    if (c.buttonText) {
      var url = normalizeUrl(c.buttonUrl);
      html += '<a class="neo-hero__button"' + (url ? ' href="' + escapeAttr(url) + '"' : '') + '>' +
        escapeHtml(c.buttonText) + '</a>';
    }
    return html + '</div></div>';
  });

  // ---- anchor-nav ---------------------------------------------
  // content: tabs [{id, label, targetSectionId}], activeColor, mode
  // (inline|overlay). Targets resolve to `#section-<targetSectionId>`, the id
  // the section pass emits — the same convention upstream's button scroll
  // handler assumes.
  // Deviation: `mode: overlay` (a floating, scroll-spy-driven bar) renders as
  // the inline bar plus a marker comment. Scroll-spy active-state tracking is
  // not ported; `activeColor` is exposed as a custom property so authored CSS
  // and :target styling can use it.
  Neo.registerType('anchor-nav', function (el) {
    var c = el.content || {};
    var tabs = itemsOf(c, 'tabs');
    if (tabs.length === 0) return '';
    var overlayNote = (c.mode === 'overlay')
      ? '<!-- neo: anchor-nav mode "overlay" rendered inline (scroll-spy overlay not ported) -->'
      : '';
    var html = mobileStyleTag(el) + overlayNote +
      wrapperOpen(el, 'content-type-neo-anchor-nav', {
        '--neo-anchor-active': c.activeColor || undefined
      });
    html += '<nav class="neo-anchor-nav' + (c.layout === 'vertical' ? ' neo-anchor-nav--vertical' : '') + '">';
    for (var i = 0; i < tabs.length; i++) {
      var target = safeId(tabs[i] && tabs[i].targetSectionId);
      html += '<a class="neo-anchor-nav__tab"' +
        (target ? ' href="#section-' + escapeAttr(target) + '"' : '') + '>' +
        escapeHtml(tabs[i] && tabs[i].label ? tabs[i].label : '') + '</a>';
    }
    return html + '</nav></div>';
  });

  // ---- marquee ------------------------------------------------
  // content: items [{text, src}], speed (30), direction (left|right),
  // pauseOnHover (true), showBorder (true). Upstream animates a single flex
  // track; `direction: right` becomes animation-direction: reverse.
  Neo.registerType('marquee', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    var speed = Number(c.speed || 30);
    var reverse = c.direction === 'right';
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-marquee');
    html += '<div class="neo-marquee' + (c.showBorder !== false ? ' neo-marquee--bordered' : '') + '">';
    html += '<div class="neo-marquee__track' + (c.pauseOnHover !== false ? ' neo-marquee__track--pausable' : '') + '"' +
      styleAttrOf({ animationDuration: speed + 's', animationDirection: reverse ? 'reverse' : 'normal' }) + '>';
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      html += '<div class="neo-marquee__item">';
      html += it.src
        ? '<img src="' + escapeAttr(it.src) + '" alt="' + escapeAttr(it.text || '') + '" loading="lazy">'
        : '<span>' + escapeHtml(it.text || '') + '</span>';
      html += '</div>';
    }
    return html + '</div></div></div>';
  });

  // ---- partner-logos ------------------------------------------
  // content: headline, logos [{name, src, url}], layout (row|grid),
  // columns (4), grayscale (true), logoHeight (sm|md|lg).
  // Authored data only — despite its `aico` palette category it makes no API call.
  Neo.registerType('partner-logos', function (el) {
    var c = el.content || {};
    var logos = itemsOf(c, 'logos');
    var size = c.logoHeight || 'md';
    var grid = c.layout === 'grid';
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-partner-logos');
    if (c.headline) html += '<h2 class="neo-partner-logos__headline">' + escapeHtml(c.headline) + '</h2>';
    html += '<div class="neo-partner-logos__list neo-partner-logos__list--' + (grid ? 'grid' : 'row') +
      (c.grayscale !== false ? ' is-grayscale' : '') + '"' +
      (grid ? styleAttrOf({ gridTemplateColumns: 'repeat(' + (Number(c.columns) || 4) + ', minmax(0, 1fr))' }) : '') + '>';
    for (var i = 0; i < logos.length; i++) {
      var lg = logos[i] || {};
      var inner = lg.src
        ? '<img class="neo-partner-logos__img neo-partner-logos__img--' + escapeAttr(size) + '" src="' +
          escapeAttr(lg.src) + '" alt="' + escapeAttr(lg.name || '') + '" loading="lazy">'
        : '<span class="neo-partner-logos__name">' + escapeHtml(lg.name || '') + '</span>';
      var url = normalizeUrl(lg.url);
      html += url
        ? '<a class="neo-partner-logos__item" href="' + escapeAttr(url) + '" target="_blank" rel="noopener noreferrer">' + inner + '</a>'
        : '<div class="neo-partner-logos__item">' + inner + '</div>';
    }
    return html + '</div></div>';
  });

  // ---- file-list ----------------------------------------------
  // content: title, subtitle, items [{id, name, fileUrl, fileType, fileSize}].
  // Deviation: upstream's editor-only upload affordances (drop zone, progress
  // bar) are omitted; the published view is a list of download links.
  Neo.registerType('file-list', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-file-list');
    if (c.title || c.subtitle) {
      html += '<div class="neo-file-list__header">';
      if (c.title) html += '<h3 class="neo-file-list__title">' + escapeHtml(c.title) + '</h3>';
      if (c.subtitle) html += '<p class="neo-file-list__subtitle">' + escapeHtml(c.subtitle) + '</p>';
      html += '</div>';
    }
    html += '<ul class="neo-file-list__items">';
    for (var i = 0; i < items.length; i++) {
      var f = items[i] || {};
      var href = normalizeUrl(f.fileUrl);
      var meta = [f.fileType, f.fileSize].filter(Boolean).join(' · ');
      html += '<li class="neo-file-list__item">' +
        (href ? '<a class="neo-file-list__link" href="' + escapeAttr(href) + '" download>' : '<span class="neo-file-list__link">') +
        '<span class="neo-file-list__icon" aria-hidden="true">' + escapeHtml(String(f.fileType || 'file').toUpperCase().slice(0, 4)) + '</span>' +
        '<span class="neo-file-list__name">' + escapeHtml(f.name || '') + '</span>' +
        (meta ? '<span class="neo-file-list__meta">' + escapeHtml(meta) + '</span>' : '') +
        (href ? '</a>' : '</span>') +
        '</li>';
    }
    return html + '</ul></div>';
  });

  // ---- social-embed -------------------------------------------
  // content: platform, embedUrl, embedCode, fallbackText.
  // embedCode wins over embedUrl, exactly as upstream; the fallback box renders
  // when neither is set.
  Neo.registerType('social-embed', function (el) {
    var c = el.content || {};
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-social-embed');
    if (c.embedCode) {
      html += '<div class="neo-social-embed__code">' + c.embedCode + '</div>';
    } else if (c.embedUrl) {
      html += '<iframe class="neo-social-embed__frame" src="' + escapeAttr(normalizeUrl(c.embedUrl)) +
        '" loading="lazy" allowfullscreen title="' + escapeAttr(c.platform || 'social') + '"></iframe>';
    } else {
      html += '<div class="neo-social-embed__fallback">' + escapeHtml(c.fallbackText || '') + '</div>';
    }
    return html + '</div>';
  });

  /** Icon frame shared by multicolumn/features: an <img> when iconUrl is set,
   *  otherwise the empty frame (lucide `icon` names cannot be resolved here). */
  function iconFrame(item, cls, bgColor) {
    var url = item && item.iconUrl;
    return '<div class="' + cls + '"' + styleAttrOf({ backgroundColor: bgColor || undefined }) + '>' +
      (url ? '<img src="' + escapeAttr(url) + '" alt="" loading="lazy">' : '') +
      '</div>';
  }

  // ---- multicolumn --------------------------------------------
  // content: columns [{icon, iconUrl, title, description}], layout
  // (horizontal|vertical), iconStyle (circle), iconColor, titleColor,
  // descriptionColor. The grid itself comes from styles.desktop (display:grid).
  Neo.registerType('multicolumn', function (el) {
    var c = el.content || {};
    var cols = itemsOf(c, 'columns');
    var horizontal = c.layout === 'horizontal';
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-multicolumn');
    for (var i = 0; i < cols.length; i++) {
      var col = cols[i] || {};
      html += '<div class="neo-multicolumn__item' + (horizontal ? ' neo-multicolumn__item--horizontal' : '') + '">';
      html += iconFrame(col, 'neo-multicolumn__icon' + (c.iconStyle === 'circle' ? ' is-circle' : ''), c.iconColor);
      html += '<div class="neo-multicolumn__body">';
      if (col.title) html += '<h4 class="neo-multicolumn__title"' + styleAttrOf({ color: c.titleColor || undefined }) + '>' + escapeHtml(col.title) + '</h4>';
      if (col.description) html += '<p class="neo-multicolumn__text"' + styleAttrOf({ color: c.descriptionColor || undefined }) + '>' + escapeHtml(col.description) + '</p>';
      html += '</div></div>';
    }
    return html + '</div>';
  });

  // ---- features -----------------------------------------------
  // content: items [{icon, iconUrl, image, title, description}],
  // featuresIconColor / featuresIconBgColor / featuresTitleColor /
  // featuresDescColor. Upstream forces
  // `gridTemplateColumns: repeat(<item count>, 1fr)` onto the wrapper.
  Neo.registerType('features', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    var html = mobileStyleTag(el) +
      wrapperOpen(el, 'content-type-neo-features', {
        gridTemplateColumns: 'repeat(' + (items.length || 3) + ', minmax(0, 1fr))'
      });
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      html += '<div class="neo-features__item">';
      if (it.image) html += '<img class="neo-features__image" src="' + escapeAttr(it.image) + '" alt="" loading="lazy">';
      html += iconFrame(it, 'neo-features__icon', c.featuresIconBgColor);
      if (it.title) html += '<h3 class="neo-features__title"' + styleAttrOf({ color: c.featuresTitleColor || undefined }) + '>' + escapeHtml(it.title) + '</h3>';
      if (it.description) html += '<p class="neo-features__text"' + styleAttrOf({ color: c.featuresDescColor || undefined }) + '>' + escapeHtml(it.description) + '</p>';
      html += '</div>';
    }
    return html + '</div>';
  });

  // ---- testimonial --------------------------------------------
  // content: items [{quote, author, role, avatar, rating}] with upstream's
  // legacy flat {quote, author, role, avatar, rating} fallback;
  // testimonialColumns, testimonialShowRating, testimonialShowAvatar,
  // testimonialCardBg / CardBorderColor / QuoteColor / AuthorColor /
  // CardRadius / CardPadding / CardShadow.
  // Deviation: `testimonialDisplayMode: slider` renders as the grid plus a
  // marker comment — the slider needs the carousel hydrator, which is Tier 2.
  var TESTIMONIAL_SHADOWS = {
    none: 'none', sm: '0 1px 2px rgba(0,0,0,0.06)',
    md: '0 4px 12px rgba(0,0,0,0.10)', lg: '0 10px 24px rgba(0,0,0,0.15)'
  };

  Neo.registerType('testimonial', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    if (items.length === 0) {
      items = [{ quote: c.quote || '', author: c.author || '', role: c.role || '', avatar: c.avatar || '', rating: c.rating == null ? 5 : c.rating }];
    }
    var columns = c.testimonialColumns == null
      ? (items.length === 1 ? 1 : Math.min(items.length, 3))
      : Number(c.testimonialColumns);
    var showRating = c.testimonialShowRating !== false;
    var showAvatar = c.testimonialShowAvatar !== false;
    var shadow = c.testimonialCardShadow || 'none';
    var cardStyle = {
      backgroundColor: c.testimonialCardBg || undefined,
      borderColor: c.testimonialCardBorderColor || undefined,
      borderRadius: c.testimonialCardRadius != null ? c.testimonialCardRadius + 'px' : undefined,
      padding: c.testimonialCardPadding != null ? c.testimonialCardPadding + 'px' : undefined,
      boxShadow: shadow !== 'none' ? TESTIMONIAL_SHADOWS[shadow] : undefined
    };
    var sliderNote = (c.testimonialDisplayMode === 'slider')
      ? '<!-- neo: testimonial displayMode "slider" rendered as a grid (carousel not ported) -->'
      : '';

    var html = mobileStyleTag(el) + sliderNote +
      wrapperOpen(el, 'content-type-neo-testimonial', {
        display: 'grid',
        gridTemplateColumns: 'repeat(' + (columns || 1) + ', minmax(0, 1fr))'
      });
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      html += '<figure class="neo-testimonial__card"' + styleAttrOf(cardStyle) + '>';
      html += '<blockquote class="neo-testimonial__quote"' + styleAttrOf({ color: c.testimonialQuoteColor || undefined }) + '>' +
        '“' + escapeHtml(it.quote || '') + '”</blockquote>';
      if (showRating && Number(it.rating || 0) > 0) {
        var stars = Math.max(0, Math.min(5, Math.round(Number(it.rating))));
        html += '<div class="neo-testimonial__rating" aria-label="' + stars + '/5">' +
          new Array(stars + 1).join('★') + new Array(6 - stars).join('☆') + '</div>';
      }
      html += '<figcaption class="neo-testimonial__author"' + styleAttrOf({ color: c.testimonialAuthorColor || undefined }) + '>';
      if (showAvatar && it.avatar) {
        html += '<img class="neo-testimonial__avatar" src="' + escapeAttr(it.avatar) + '" alt="" loading="lazy">';
      }
      html += '<span class="neo-testimonial__name">' + escapeHtml(it.author || '') + '</span>';
      if (it.role) html += '<span class="neo-testimonial__role">' + escapeHtml(it.role) + '</span>';
      html += '</figcaption></figure>';
    }
    return html + '</div>';
  });

  /**
   * faq / accordion / feature-accordion share one disclosure builder.
   *
   * Deviation: upstream uses the Radix Accordion primitive (a
   * `h3 > button[data-state]` tree driven by JS). This emits native
   * <details>/<summary>, which is the semantic equivalent with zero JS and
   * working keyboard/AT behaviour. Exclusive mode is the HTML `name` attribute
   * (one open at a time); browsers without it simply allow multiple open,
   * which is the graceful direction to fail.
   *
   * `opts.richText` mirrors how upstream treats the fields: faq renders its
   * question/answer through React (escaped), while accordion and
   * feature-accordion go through InlineEditableField, which is a TipTap
   * rich-text field written out with dangerouslySetInnerHTML.
   */
  function disclosureList(el, items, titleKey, bodyKey, opts) {
    var options = opts || {};
    var groupName = 'neo-acc-' + safeId(el.id);
    var exclusive = !options.allowMultiple;
    var out = options.richText ? function (v) { return v == null ? '' : String(v); } : escapeHtml;
    var html = '<div class="neo-disclosure">';
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var open = (options.firstOpen && i === 0) ? ' open' : '';
      html += '<details class="neo-disclosure__item"' +
        (exclusive ? ' name="' + escapeAttr(groupName) + '"' : '') + open +
        styleAttrOf({ backgroundColor: options.itemBg || undefined, borderColor: options.itemBorderColor || undefined }) + '>' +
        '<summary class="neo-disclosure__summary"' + styleAttrOf({ color: options.titleColor || undefined }) + '>' +
        out(it[titleKey] || '') + '</summary>' +
        '<div class="neo-disclosure__body"' + styleAttrOf({ color: options.bodyColor || undefined }) + '>' +
        out(it[bodyKey] || '') + '</div>' +
        '</details>';
    }
    return html + '</div>';
  }

  // ---- faq ----------------------------------------------------
  // content: items [{question, answer}], faqAllowMultiple (false),
  // faqFirstOpen (false), faqItemBg, faqItemBorderColor, faqQuestionColor,
  // faqAnswerColor.
  Neo.registerType('faq', function (el) {
    var c = el.content || {};
    return mobileStyleTag(el) +
      wrapperOpen(el, 'content-type-neo-faq') +
      disclosureList(el, itemsOf(c), 'question', 'answer', {
        allowMultiple: c.faqAllowMultiple === true,
        firstOpen: c.faqFirstOpen === true,
        itemBg: c.faqItemBg,
        itemBorderColor: c.faqItemBorderColor,
        titleColor: c.faqQuestionColor,
        bodyColor: c.faqAnswerColor
      }) +
      '</div>';
  });

  // ---- accordion ----------------------------------------------
  // content: items [{title, content}], allowMultiple (false).
  Neo.registerType('accordion', function (el) {
    var c = el.content || {};
    return mobileStyleTag(el) +
      wrapperOpen(el, 'content-type-neo-accordion') +
      disclosureList(el, itemsOf(c), 'title', 'content', { allowMultiple: c.allowMultiple === true, richText: true }) +
      '</div>';
  });

  // ---- feature-accordion --------------------------------------
  // content: heading, items [{title, content}], image,
  // featureAccordionColumns ('40/60'), featureAccordionGap ('48px'),
  // featureAccordionDivider (false).
  Neo.registerType('feature-accordion', function (el) {
    var c = el.content || {};
    var ratio = String(c.featureAccordionColumns || '40/60').split('/');
    var left = parseInt(ratio[0], 10) || 40;
    var right = parseInt(ratio[1], 10) || (100 - left);
    var html = mobileStyleTag(el) + wrapperOpen(el, 'content-type-neo-feature-accordion');
    if (c.heading) html += '<h2 class="neo-feature-accordion__heading">' + escapeHtml(c.heading) + '</h2>';
    html += '<div class="neo-feature-accordion__grid"' + styleAttrOf({
      display: 'grid',
      gridTemplateColumns: left + 'fr ' + right + 'fr',
      gap: c.featureAccordionGap || '48px'
    }) + '>';
    html += '<div class="neo-feature-accordion__media">' +
      (c.image ? '<img src="' + escapeAttr(c.image) + '" alt="" loading="lazy">' : '') + '</div>';
    html += '<div class="neo-feature-accordion__items' + (c.featureAccordionDivider ? ' has-divider' : '') + '">' +
      disclosureList(el, itemsOf(c), 'title', 'content', { richText: true }) + '</div>';
    return html + '</div></div>';
  });

  // ============================================================
  // 13. image — the content bag the engine drops
  //
  // The engine's `image` renderer forwards `content.src/alt/caption` plus
  // `styles.desktop`, and nothing else. Everything aiconeo keeps on `content`
  // is lost. ElementRenderer's `case 'image'` (public-core bundle) computes:
  //
  //   clip-path  from imageShapeEnabled / …Type / …Edge / …Direction /
  //              …Intensity — the "shape" picker in the image inspector.
  //              /pages/hospitalites authors diagonal / both / left / 9 on its
  //              match photo, i.e. polygon(0 9%, 100% 0, 100% 91%, 0 100%),
  //              which reads as a slight tilt. The storefront drew a plain
  //              rectangle — the bug this section exists for.
  //   object-fit / object-position (+ scale() when cropZoom > 1) from
  //              objectFit / objectPosition / cropX / cropY / cropZoom.
  //   a scrim    <div class="absolute inset-0"> filled with
  //              rgba(0,0,0, overlayOpacity/100).
  //
  // Ported here DATA-DRIVEN: an element that authors none of them renders the
  // exact markup the engine produced before, down to the attribute order, so
  // the 20 documents whose global <style> blocks target `.x__img img` are
  // untouched. The clip is applied to the <img> rather than to an inserted
  // wrapper (aiconeo's own shape carries a wrapper) precisely to keep that DOM
  // contract: clip-path on a replaced element paints identically and, unlike a
  // transform, cannot overflow its parent, so no page needs extra room for it.
  //
  // Two builder DEFAULTS are deliberately NOT ported — they are editor
  // opinions that every LS document already fights:
  //   * `overlayOpacity ?? 30`, a 30% black scrim over EVERY image: 33 of the
  //     34 authored values across the 29 live documents are an explicit `0`
  //     written to switch it off, and pages 14/19/23 carry extra CSS hiding
  //     `div.absolute.inset-0` as a belt to that suspender. Honouring the
  //     default would darken the 25 images authored before it was found. The
  //     scrim is emitted only when the document asks for one (> 0).
  //   * `objectFit ?? 'cover'`, for the same reason (page 14's transparent
  //     logo has CSS undoing it).
  // Consequence for authors: keep writing an explicit `overlayOpacity: 0` in
  // the builder, so its canvas shows what the storefront will show.
  // ============================================================

  // aiconeo's named intensities, for documents that store a preset not a number.
  var IMAGE_SHAPE_INTENSITY = { sm: 5, md: 10, lg: 18 };

  function shapeIntensity(raw) {
    if (typeof raw === 'number' && isFinite(raw)) return raw;
    if (typeof raw === 'string' && IMAGE_SHAPE_INTENSITY[raw] !== undefined) {
      return IMAGE_SHAPE_INTENSITY[raw];
    }
    return 10;
  }

  /** The four shapes ElementRenderer can produce. '' when the element has none. */
  function imageClipPath(c) {
    if (!c || !c.imageShapeEnabled) return '';
    var type = c.imageShapeType || 'rectangle';
    if (type === 'rectangle') return '';

    var edge = c.imageShapeEdge || 'bottom';
    var dir = c.imageShapeDirection || 'left';
    var i = shapeIntensity(c.imageShapeIntensity);
    var top = edge === 'top' || edge === 'both';
    var bottom = edge === 'bottom' || edge === 'both';

    if (type === 'diagonal' || type === 'soft-diagonal') {
      return 'polygon(' +
        '0 ' + (top && dir === 'left' ? i : 0) + '%, ' +
        '100% ' + (top ? (dir === 'left' ? 0 : i) : 0) + '%, ' +
        '100% ' + (bottom && dir === 'left' ? 100 - i : 100) + '%, ' +
        '0 ' + (bottom ? (dir === 'left' ? 100 : 100 - i) : 100) + '%)';
    }
    if (type === 'wave') {
      var wt = top ? i : 0;
      var wb = bottom ? i : 0;
      return 'polygon(' +
        (top ? '0% ' + wt + '%, 25% 0%, 50% ' + wt + '%, 75% 0%, 100% ' + wt + '%'
          : '0% 0%, 100% 0%') + ', ' +
        (bottom ? '100% ' + (100 - wb) + '%, 75% 100%, 50% ' + (100 - wb) + '%, 25% 100%, 0% ' + (100 - wb) + '%'
          : '100% 100%, 0% 100%') + ')';
    }
    if (type === 'curve') {
      var ct = top ? i : 0;
      var cb = bottom ? i : 0;
      return 'polygon(' +
        (top ? '0% ' + ct + '%, 10% ' + (ct * 0.3) + '%, 30% 0%, 50% 0%, 70% 0%, 90% ' + (ct * 0.3) + '%, 100% ' + ct + '%'
          : '0% 0%, 100% 0%') + ', ' +
        (bottom ? '100% ' + (100 - cb) + '%, 90% ' + (100 - cb * 0.3) + '%, 70% 100%, 50% 100%, 30% 100%, 10% ' + (100 - cb * 0.3) + '%, 0% ' + (100 - cb) + '%'
          : '100% 100%, 0% 100%') + ')';
    }
    return '';
  }

  /** object-fit / object-position / crop zoom, only for what the doc authored. */
  function imageFitStyles(c) {
    var out = {};
    if (!c) return out;

    var zoom = Number(c.cropZoom);
    var hasZoom = isFinite(zoom) && zoom > 1;
    var x = typeof c.cropX === 'number' ? c.cropX : 50;
    var y = typeof c.cropY === 'number' ? c.cropY : 50;
    var cropped = hasZoom || x !== 50 || y !== 50;

    if (c.objectFit) out.objectFit = c.objectFit;
    if (cropped) {
      // A crop only means something against a fitted box, so it implies cover.
      if (!out.objectFit) out.objectFit = 'cover';
      out.objectPosition = x + '% ' + y + '%';
    } else if (c.objectPosition) {
      out.objectPosition = c.objectPosition;
    }
    if (hasZoom) {
      out.transform = 'scale(' + zoom + ')';
      out.transformOrigin = x + '% ' + y + '%';
    }
    return out;
  }

  Neo.registerType('image', function (el) {
    var c = (el && el.content) || {};
    var src = c.src || '';
    if (!src) return '';

    var bag = {};
    var desktop = pickStyles(el, 'desktop');
    for (var k in desktop) {
      if (Object.prototype.hasOwnProperty.call(desktop, k)) bag[k] = desktop[k];
    }

    var clip = imageClipPath(c);
    if (clip) bag.clipPath = clip;

    var fit = imageFitStyles(c);
    for (var f in fit) {
      if (Object.prototype.hasOwnProperty.call(fit, f)) bag[f] = fit[f];
    }

    var opacity = Number(c.overlayOpacity);
    var scrim = '';
    if (isFinite(opacity) && opacity > 0) {
      // Sits inside .content-type-neo-image, which the stylesheet makes a
      // containing block for it; the clip is repeated so the scrim follows the
      // shape instead of squaring off the corners the image just cut away.
      var scrimStyles = { backgroundColor: 'rgba(0,0,0,' + (opacity / 100) + ')' };
      if (clip) scrimStyles.clipPath = clip;
      scrim = '<span class="neo-image__scrim" aria-hidden="true"' + styleAttrOf(scrimStyles) + '></span>';
    }

    var caption = c.caption || '';

    return mobileStyleTag(el) +
      '<div class="content-type-neo-image' + (scrim ? ' content-type-neo-image--scrim' : '') + '">' +
      '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(c.alt || '') + '" loading="lazy"' +
      dataElAttr(el) + styleAttrOf(bag) + '>' +
      scrim +
      (caption ? '<span class="caption">' + caption + '</span>' : '') +
      '</div>';
  });

  // ============================================================
  // 3. nested columns (section-level `columns` stays with the engine)
  // ============================================================

  function renderNestedColumns(el) {
    var content = (el && el.content) || {};
    var count = parseInt(content.columnCount, 10);
    if (isNaN(count) || count < 1) count = 2;
    if (count > 4) count = 4;

    var children = Array.isArray(el.children) ? el.children : [];
    var buckets = [];
    for (var c = 0; c < count; c++) buckets.push([]);

    var fallbackIdx = 0;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var ci = (child && child.content && typeof child.content.columnIndex === 'number')
        ? child.content.columnIndex
        : fallbackIdx++;
      if (ci < 0) ci = 0;
      if (ci >= count) ci = count - 1;
      buckets[ci].push(child);
    }

    var columnsHtml = '';
    for (var col = 0; col < count; col++) {
      var inner = '';
      for (var k = 0; k < buckets[col].length; k++) inner += renderChild(buckets[col][k]);
      columnsHtml += '<div class="content-column column__' + (col + 1) + '">' + inner + '</div>';
    }

    return mobileStyleTag(el) +
      '<div class="content-row total-columns-' + count + '"' + dataElAttr(el) + inlineStyleAttr(el) + '>' +
      columnsHtml +
      '</div>';
  }

  // ============================================================
  // 4. linked-form
  //
  // The element carries only {formId, formName, submitEndpoint, showTitle,
  // showDescription} — NOT the fields. aiconeo's own renderer reads them from
  // an authenticated admin endpoint, which a storefront cannot use, so the
  // field list is fetched at hydrate time from the PUBLIC, CORS-enabled read
  // endpoint (Modules/AicoForms/Routes/api.php → `aiconeo/public/aico-forms/{id}`,
  // throttle:api-read, allow-listed output).
  //
  // Submission uses the LEGACY public endpoint baked into the element as
  // `submitEndpoint`: a NATIVE multipart form POST (no fetch), because it
  // answers 302 back to `redirect_to` with ?isSuccessful=true / ?isError=true —
  // a top-level form navigation follows that redirect without needing CORS.
  // Contract: docs/aico-neo-pages.md.
  // ============================================================

  Neo.registerType('linked-form', function (el) {
    var content = (el && el.content) || {};
    var formId = content.formId || content.linkedFormId;
    if (!formId) {
      warnUnknownOnce('linked-form(no formId)');
      return '';
    }
    var endpoint = content.submitEndpoint || FALLBACK_SUBMIT_ENDPOINT;
    // element.className is added by the registry decorator (section 7), not here.
    var classes = ['content-type-neo-linked-form', 'aico-custom-form-wrap'];

    return mobileStyleTag(el) +
      '<div class="' + escapeAttr(classes.join(' ')) + '"' + dataElAttr(el) + inlineStyleAttr(el) +
      ' data-neo-form="1"' +
      ' data-neo-form-id="' + escapeAttr(formId) + '"' +
      ' data-neo-form-endpoint="' + escapeAttr(endpoint) + '"' +
      ' data-neo-form-title="' + escapeAttr(content.showTitle === true ? '1' : '') + '"' +
      '>' +
      '<div class="aico-form-banner aico-form-banner--success d-none" role="status"></div>' +
      '<div class="aico-form-banner aico-form-banner--error d-none" role="alert"></div>' +
      '<div class="aico-form-slot"></div>' +
      '</div>';
  });

  /** Derive the public form-definition URL from the authored submit endpoint,
   *  so the aico host + tenant prefix are never hardcoded here.
   *  …/<tenant>/api/v1/news/form-submissions → …/<tenant>/api/aiconeo/public/aico-forms/<id> */
  function formReadUrl(submitEndpoint, formId) {
    var base = String(submitEndpoint || FALLBACK_SUBMIT_ENDPOINT)
      .replace(/\/api\/v1\/news\/form-submissions\/?$/, '')
      .replace(/\/+$/, '');
    return base + '/api/aiconeo/public/aico-forms/' + encodeURIComponent(formId);
  }

  /** Map an aico form element type to an HTML input type / control kind. */
  function controlFor(type) {
    switch (String(type || '').toLowerCase()) {
      case 'textarea':
      case 'text_area': return { kind: 'textarea' };
      case 'dropdown': return { kind: 'select' };
      case 'radio': return { kind: 'radio' };
      case 'checkbox': return { kind: 'checkbox' };
      case 'file_upload':
      case 'multiple_file_upload': return { kind: 'input', inputType: 'file' };
      case 'email': return { kind: 'input', inputType: 'email' };
      case 'phone':
      case 'tel': return { kind: 'input', inputType: 'tel' };
      case 'number': return { kind: 'input', inputType: 'number' };
      case 'date': return { kind: 'input', inputType: 'date' };
      default: return { kind: 'input', inputType: 'text' };
    }
  }

  function fieldHtml(field, formId) {
    var control = controlFor(field.type);
    var name = escapeAttr(field.key);
    var id = 'aico-form-' + escapeAttr(formId) + '-' + escapeAttr(field.key);
    var label = escapeHtml(field.label || field.key);
    var required = field.isRequired ? ' required' : '';
    var requiredMark = field.isRequired ? ' <span class="aico_label__required" aria-hidden="true">*</span>' : '';
    var half = String(field.columnSize || '').toUpperCase() === 'HALF_ROW';
    var options = (field.config && field.config.possibleValues) || [];

    var body;
    if (control.kind === 'textarea') {
      body = '<textarea class="aico_input" id="' + id + '" name="' + name + '" rows="5"' + required + '></textarea>';
    } else if (control.kind === 'select') {
      body = '<select class="aico_input" id="' + id + '" name="' + name + '"' + required + '>' +
        '<option value=""></option>';
      for (var i = 0; i < options.length; i++) {
        var v = escapeAttr(options[i].value);
        body += '<option value="' + v + '">' + escapeHtml(options[i].label || options[i].value) + '</option>';
      }
      body += '</select>';
    } else if (control.kind === 'radio') {
      body = '<div class="aico_options">';
      for (var r = 0; r < options.length; r++) {
        var rid = id + '-' + r;
        body += '<label class="aico_option" for="' + rid + '">' +
          '<input type="radio" id="' + rid + '" name="' + name + '" value="' + escapeAttr(options[r].value) + '"' + required + '>' +
          '<span>' + escapeHtml(options[r].label || options[r].value) + '</span></label>';
      }
      body += '</div>';
    } else if (control.kind === 'checkbox') {
      // Single opt-in checkbox: the label sits next to the box, not above it.
      return '<div class="form-item form-item--checkbox' + (half ? ' form-item--half' : '') + '">' +
        '<label class="aico_label aico_label--inline" for="' + id + '">' +
        '<input type="checkbox" id="' + id + '" name="' + name + '" value="1"' + required + '>' +
        '<span>' + label + requiredMark + '</span></label>' +
        '</div>';
    } else {
      body = '<input class="aico_input" type="' + control.inputType + '" id="' + id + '" name="' + name + '"' + required + '>';
    }

    return '<div class="form-item' + (half ? ' form-item--half' : '') + '">' +
      '<label class="aico_label" for="' + id + '">' + label + requiredMark + '</label>' +
      body +
      '</div>';
  }

  /** Current URL without the isSuccessful/isError params, so a resubmit after a
   *  redirect doesn't stack query flags. */
  function redirectTarget() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('isSuccessful');
      url.searchParams.delete('isError');
      return url.toString();
    } catch (e) {
      return window.location.href;
    }
  }

  // i18n via window.AICO_NEO_I18N — a small per-locale object emitted by
  // snippets/aico-neo-content.liquid (the FCZ shop serves de/fr/en from one
  // storefront, so the strings cannot live in this static asset). German
  // fallback inline, matching the shop's primary locale.
  function lsT(key, fallback) { return (window.AICO_NEO_I18N && window.AICO_NEO_I18N[key]) || fallback; }

  function buildForm(host, form) {
    var formId = host.getAttribute('data-neo-form-id');
    var endpoint = host.getAttribute('data-neo-form-endpoint');
    var elements = (form && Array.isArray(form.elements)) ? form.elements : [];

    var fields = elements
      .filter(function (f) { return f && !f.isHidden && f.type !== 'title' && f.type !== 'html' && f.key; })
      .sort(function (a, b) { return (a.position || 0) - (b.position || 0); });

    if (fields.length === 0) {
      warnUnknownOnce('linked-form(' + formId + ') has no visible fields');
      return;
    }

    // No `novalidate` — native browser validation is the only client-side check.
    var html = '<form class="aico-custom-form" method="post" enctype="multipart/form-data" action="' +
      escapeAttr(endpoint) + '">' +
      '<input type="hidden" name="form_id" value="' + escapeAttr(formId) + '">' +
      '<input type="hidden" name="redirect_to" value="' + escapeAttr(redirectTarget()) + '">';

    if (host.getAttribute('data-neo-form-title') === '1' && form.name) {
      html += '<h3 class="aico-custom-form__title">' + escapeHtml(form.name) + '</h3>';
    }

    html += '<div class="aico-custom-form__fields">';
    for (var i = 0; i < fields.length; i++) html += fieldHtml(fields[i], formId);
    html += '</div>';

    html += '<button type="submit" class="aico-custom-form__submit">' +
      escapeHtml(form.submissionText || lsT('common.send', 'Senden')) + '</button>';
    html += '</form>';

    var slot = host.querySelector('.aico-form-slot');
    if (slot) slot.innerHTML = html;
  }

  /**
   * The legacy submit answers 302 back to `redirect_to` with ?isSuccessful=true
   * or ?isError=true, so the outcome is read off the query string on load.
   * Copy comes from window.AICO_NEO_I18N (snippets/aico-neo-content.liquid)
   * with the German literal as fallback.
   */
  function showBanners(host) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var success = host.querySelector('.aico-form-banner--success');
    var error = host.querySelector('.aico-form-banner--error');

    if (params.get('isSuccessful') === 'true' && success) {
      success.textContent = lsT('js.form_success', 'Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet.');
      success.classList.remove('d-none');
    } else if (params.get('isError') === 'true' && error) {
      error.textContent = lsT('js.form_error', 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      error.classList.remove('d-none');
    }
  }

  function hydrateForms(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var hosts = scope.querySelectorAll('[data-neo-form="1"]');
    for (var i = 0; i < hosts.length; i++) {
      (function (host) {
        if (host.getAttribute('data-neo-form-hydrated') === '1') return;
        host.setAttribute('data-neo-form-hydrated', '1');

        var formId = host.getAttribute('data-neo-form-id');
        var url = formReadUrl(host.getAttribute('data-neo-form-endpoint'), formId);

        fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .then(function (payload) {
            var form = (payload && payload.data) ? payload.data : payload;
            buildForm(host, form);
            showBanners(host);
          })
          .catch(function (error) {
            if (window.console && console.warn) {
              console.warn('content-builder-neo-ext: could not load aico form ' + formId, error);
            }
            host.setAttribute('data-neo-form-hydrated', '0');
          });
      })(hosts[i]);
    }
  }

  // ============================================================
  // 2. Unknown-with-children degradation
  //
  // renderUnknown is internal to the engine, so instead of patching it the
  // document is normalised on the way into render(): an unregistered type that
  // carries children becomes a `container`, keeping its styles and subtree.
  // ============================================================

  /**
   * Tally of element types the registry could not render, for the aggregated
   * warning at the end of each render. Reset per Neo.render call.
   */
  var unrenderedTally = null;

  function tallyUnrendered(type) {
    if (!unrenderedTally) unrenderedTally = {};
    unrenderedTally[type] = (unrenderedTally[type] || 0) + 1;
  }

  /**
   * Placeholder for an unrenderable LEAF. aiconeo defines 58 element types and
   * this renderer implements a subset, so the rest would otherwise vanish with
   * nothing in the DOM to explain the hole. Emitting a comment makes "why is
   * this block missing" a view-source away instead of a bisect.
   */
  Neo.registerType('neo-unrendered', function (el) {
    return '<!-- neo: unrendered type "' + String(el.__neoOriginalType || 'unknown').replace(/--+/g, '-') +
      '" id="' + safeId(el.id) + '" -->';
  });

  function normalizeUnknown(node) {
    if (!node || typeof node !== 'object') return node;

    var children = Array.isArray(node.children) ? node.children
      : Array.isArray(node.elements) ? node.elements
        : null;
    if (children) {
      for (var i = 0; i < children.length; i++) normalizeUnknown(children[i]);
    }

    var type = node.type;
    if (typeof type !== 'string' ||
      type === 'columns' ||
      typeof Neo._renderers[type] === 'function') {
      return node;
    }

    tallyUnrendered(type);

    if (children && children.length > 0) {
      // Wrapper-ish: degrade to a container so the subtree survives.
      node.type = 'container';
      node.children = children;
    } else {
      // Leaf: nothing to fall back to — leave a marker in place of the element.
      node.__neoOriginalType = type;
      node.type = 'neo-unrendered';
    }
    return node;
  }

  /** One warning per render listing every type that could not be rendered. */
  function reportUnrendered() {
    if (!unrenderedTally) return;
    var types = Object.keys(unrenderedTally);
    if (types.length === 0) return;
    if (window.console && console.warn) {
      var summary = types.sort().map(function (t) {
        return t + ' ×' + unrenderedTally[t];
      }).join(', ');
      console.warn('content-builder-neo-ext: ' + types.length +
        ' unrendered Neo element type(s) on this page: ' + summary +
        '. Search the DOM for "neo: unrendered type" to locate them.');
    }
    unrenderedTally = null;
  }

  // ============================================================
  // 5. Section pass — styles, layout mode, page settings, scheduling
  //
  // The engine has NO section wrapper: renderSection() emits bare `.content-row`
  // divs and render() concatenates them, so `section.styles` (backgrounds,
  // padding) and `section.layout` were dropped entirely — which is why two of
  // the three Club des 130 sections looked blank (cream text on cream, the
  // authored navy never applied).
  //
  // Rather than parse the engine's output, render() is re-run PER SECTION and
  // each result is wrapped here. The engine stays the authority on element
  // markup; only the missing outer shell is added.
  //
  // Structure mirrors aiconeo's PublicPageRenderer.renderRegularSection:
  //   <div section styles>            ← background + padding, full width
  //     <div max-width: outerMaxWidth; margin: 0 auto>
  //       <div max-width: contentMaxWidth; margin: 0 auto; padding-top: …>
  //
  // Layout modes follow utils/sectionLayout.ts::resolveSectionLayout exactly:
  //   full      → outer 100%, optional innerContainer constrains the content
  //   custom    → outer = layout.maxWidth || page maxWidth || 1200px
  //   contained → outer = page maxWidth || 1200px  (layout.maxWidth is NOT read
  //               in this mode upstream, so it is not read here either — the
  //               builder preview is the contract)
  // The mobile clamps upstream (`min(100%, X)`) only exist because the builder
  // simulates viewports inside a fixed-width canvas; in a real browser a
  // max-width can never exceed the viewport, so they are no-ops here.
  // ============================================================

  var DEFAULT_PAGE_MAX_WIDTH = '1200px';

  // Page-level settings of the document currently being rendered, read by
  // wrapSection for the default max-width. Set at the top of Neo.render.
  var currentPageSettings = null;

  // Mirrors buildSectionCss's skipKeys: these are not section box styles.
  var SECTION_STYLE_SKIP = {
    borderRadiusEnabled: true,
    borderRadiusScope: true,
    cardBorderRadius: true,
    contentPaddingTop: true
  };

  function sectionBoxStyles(section, breakpoint) {
    var raw = pickStyles(section, breakpoint);
    var out = {};
    for (var k in raw) {
      if (!Object.prototype.hasOwnProperty.call(raw, k)) continue;
      if (SECTION_STYLE_SKIP[k]) continue;
      out[k] = raw[k];
    }
    return out;
  }

  function resolveSectionLayout(section, pageSettings) {
    var pageMaxWidth = (pageSettings && pageSettings.maxWidth) || DEFAULT_PAGE_MAX_WIDTH;
    var layout = (section && section.layout) || {};
    var mode = layout.mode || (section && section.fullWidth ? 'full' : 'contained');

    if (mode === 'full') {
      var inner = layout.innerContainer;
      return {
        outerMaxWidth: '100%',
        innerMaxWidth: (inner && inner.enabled) ? (inner.maxWidth || pageMaxWidth) : null
      };
    }
    if (mode === 'custom') {
      return { outerMaxWidth: layout.maxWidth || pageMaxWidth, innerMaxWidth: null };
    }
    return { outerMaxWidth: pageMaxWidth, innerMaxWidth: null };
  }

  /** Mirrors utils/timeVisibilityCheck.ts::isSectionTimeHidden. */
  function isSectionTimeHidden(section, now) {
    if (!section) return false;
    var from = section.visibleFrom ? Date.parse(section.visibleFrom) : NaN;
    var until = section.visibleUntil ? Date.parse(section.visibleUntil) : NaN;
    if (!isNaN(from) && now < from) return true;
    if (!isNaN(until) && now > until) return true;
    return false;
  }

  function wrapSection(section, innerHtml) {
    var id = safeId(section && section.id);
    var pageSettings = currentPageSettings;
    var layout = resolveSectionLayout(section, pageSettings);

    var desktop = sectionBoxStyles(section, 'desktop');
    var inlineCss = stylesToInline(desktop);
    // A rounded section must clip its children, same as buildSectionCss.
    if (desktop.borderRadius) inlineCss += (inlineCss ? ';' : '') + 'overflow:hidden';

    // styles.mobile → scoped @media override (the engine only inlines desktop).
    var mobileCss = stylesToInline(sectionBoxStyles(section, 'mobile'));
    var styleTag = '';
    if (mobileCss && id) {
      var declarations = mobileCss.split(';').filter(Boolean).map(function (d) {
        return d + ' !important';
      }).join(';');
      styleTag = '<style>@media screen and (max-width: 749px){' +
        '[data-neo-section="' + id + '"]{' + declarations + '}}</style>';
    }

    // Historic upstream default: 40px unless the author set contentPaddingTop
    // ('' means explicitly none).
    var contentPaddingTop = pickStyles(section, 'desktop').contentPaddingTop;
    if (contentPaddingTop === undefined) contentPaddingTop = '40px';

    var outerStyle = 'max-width:' + layout.outerMaxWidth + ';margin:0 auto';
    var contentStyle = 'width:100%;margin:0 auto';
    if (layout.innerMaxWidth) contentStyle += ';max-width:' + layout.innerMaxWidth;
    if (contentPaddingTop) contentStyle += ';padding-top:' + contentPaddingTop;

    return styleTag +
      '<div class="aico-neo-section"' +
      (id ? ' id="section-' + id + '" data-neo-section="' + id + '"' : '') +
      (inlineCss ? ' style="' + escapeAttr(inlineCss) + '"' : '') + '>' +
      '<div class="aico-neo-section__outer" style="' + escapeAttr(outerStyle) + '">' +
      '<div class="aico-neo-section__content" style="' + escapeAttr(contentStyle) + '">' +
      innerHtml +
      '</div></div></div>';
  }

  /**
   * 9. SECTION-LEVEL `columns` — closing the registry bypass.
   *
   * renderSection intercepts `el.type === 'columns'` and calls its internal
   * renderColumnsRow, which never touches Neo._renderers — so a section-level
   * columns element is the one thing the registry decorator cannot reach. Worse,
   * renderColumnsRow emits a bare `<div class="content-row …">` with NO
   * inlineStyleAttr at all, so the element's authored styles are dropped too.
   * On club-des-130 that silently lost `display:grid; gap:48px;
   * align-items:center` (falling back to the stylesheet's flex + 1.5rem gap)
   * plus the `le-club` anchor id.
   *
   * Reaching it needs either a core patch or surgery on the engine's output.
   * This is the surgery, kept as narrow and fail-safe as possible: replay the
   * engine's own row-index bookkeeping to compute the exact opening tag the row
   * will have, then match that one literal string. If it is not found — because
   * the engine changed its markup — nothing happens and behaviour is unchanged.
   *
   * Deviation from ElementRenderer: customId goes ON the row div rather than in
   * an extra wrapping div. Locating the row's matching close tag would mean
   * balancing tags in a string; for an anchor target the scroll behaviour is
   * identical, and className still lands on the element's own wrapper so
   * `div:has(> .x)` selectors keep working.
   */
  function decorateSectionColumns(section, html) {
    var elements = (section && section.elements) || [];
    var isFullWidth = !!(section && section.fullWidth);
    var rowIndex = 1; // a single-section render always restarts at 1
    var pending = 0;  // elements buffered into the current stacking row

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;

      if (el.type !== 'columns') {
        pending++;
        continue;
      }

      // Mirrors renderSection: a non-empty buffer is flushed as its own row first.
      if (pending > 0) { rowIndex++; pending = 0; }

      var count = parseInt(el.content && el.content.columnCount, 10);
      if (isNaN(count) || count < 1) count = 2;
      if (count > 4) count = 4;

      var classes = 'content-row row__' + rowIndex + ' total-columns-' + count +
        (isFullWidth ? ' fullwidth' : '');
      var needle = '<div class="' + classes + '">';

      var cls = el.className ? String(el.className).trim() : '';
      var customId = sanitizeCustomId(el.customId);
      var styleAttr = inlineStyleAttr(el);

      if ((cls || customId || styleAttr) && html.indexOf(needle) !== -1) {
        var replacement = mobileStyleTag(el) +
          '<div class="' + classes + (cls ? ' ' + escapeAttr(cls) : '') + '"' +
          (customId ? ' id="' + escapeAttr(customId) + '"' : '') +
          dataElAttr(el) + styleAttr + '>';
        html = html.replace(needle, replacement);
      }

      rowIndex++;
    }

    return html;
  }

  /**
   * The engine numbers rows `row__1..N` per render() call, so a per-section call
   * restarts at 1. Renumber so the concatenated document keeps the continuous
   * numbering the engine would have produced on its own.
   */
  function renumberRows(html, offset) {
    var max = 0;
    var out = html.replace(/\brow__(\d+)\b/g, function (_m, n) {
      var v = parseInt(n, 10);
      if (v > max) max = v;
      return 'row__' + (v + offset);
    });
    return { html: out, rowCount: max };
  }

  var originalRender = Neo.render;
  Neo.render = function (data) {
    var neo = data && data.contentBuilderNeoJson;
    var sections = (neo && Array.isArray(neo.sections)) ? neo.sections : [];
    currentPageSettings = (neo && neo.settings) || null;
    unrenderedTally = null;

    try {
      for (var s = 0; s < sections.length; s++) {
        var elements = (sections[s] && Array.isArray(sections[s].elements)) ? sections[s].elements : [];
        for (var e = 0; e < elements.length; e++) normalizeUnknown(elements[e]);
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: unknown-type normalisation failed', err);
      }
      return originalRender(data);
    }

    var now = Date.now();
    var html = '';
    var rowOffset = 0;

    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (!section) continue;
      if (isSectionTimeHidden(section, now)) continue;

      // One engine pass per section: the engine owns the element markup, this
      // file only supplies the wrapper it never had.
      var rendered = originalRender({ contentBuilderNeoJson: { sections: [section] } });
      rendered = decorateSectionColumns(section, rendered);
      var renumbered = renumberRows(rendered, rowOffset);
      rowOffset += renumbered.rowCount;
      html += wrapSection(section, renumbered.html);
    }

    reportUnrendered();
    return wrapPage(html, currentPageSettings);
  };

  /**
   * Page-level shell. `settings.backgroundColor` is applied explicitly rather
   * than relying on it coincidentally matching the theme's own background, and
   * `settings.accentColor` is exposed as a custom property so authored `html`
   * elements can reference it.
   */
  function wrapPage(html, pageSettings) {
    var settings = pageSettings || {};
    var css = '';
    if (settings.backgroundColor) css += 'background-color:' + settings.backgroundColor + ';';
    if (settings.accentColor) css += '--aico-neo-accent:' + settings.accentColor + ';';
    return '<div class="aico-neo-page"' +
      (css ? ' style="' + escapeAttr(css) + '"' : '') + '>' + html + '</div>';
  }

  // ============================================================
  // 12. Collapsed size containers (`container-type: inline-size`)
  //
  // aiconeo authors write their responsive systems as container queries: the
  // element gets `container-type: inline-size` (via its className, see §7) and
  // the page's in-mount <style> carries the @container rules. `container-type`
  // implies `contain: inline-size`, i.e. the element's intrinsic inline size
  // contribution becomes 0 — which is fine in a block/grid/stretch context, and
  // fatal in a shrink-to-fit one. aiconeo containers are inline-styled
  // `display:flex; flex-direction:column; align-items:flex-start`, so a size
  // container inside one is a shrink-to-fit flex item: fit-content resolves to
  // 0px and the whole prose block renders ONE WORD PER LINE
  // (/pages/organisation was 11 724px tall and unreadable; same on
  // nos-engagements, palmares, benevoles — 18 blocks in all).
  //
  // The authored guard `div:has(> .org-w){width:100%!important}` targets the
  // PARENT, not the size container itself, so it never helped.
  //
  // Why this is JS and not a stylesheet rule: CSS cannot select "an element
  // that declares container-type", so a CSS fix has to guess at wrappers by
  // class and would restyle healthy pages (an html block that is a shrink-to-fit
  // item of a flex ROW is supposed to size to its content). Measuring instead
  // keeps the fix exactly as wide as the bug: an element is only touched when it
  // IS a size container AND has already collapsed. Nothing to undo, no class
  // names, no per-page code.
  //
  // Cheap by construction: one batched read pass (rect first, computed style
  // only for the handful of candidates), then one write pass. Repeats while it
  // keeps finding new ones, because fixing an outer container can reveal a
  // collapsed inner one, capped at 3 passes.
  // ============================================================

  var SIZE_GUARD_ATTR = 'data-neo-size-guard';

  function collapsedSizeContainerPass(root) {
    var nodes = root.querySelectorAll('*');
    var collapsed = [];

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.hasAttribute(SIZE_GUARD_ATTR)) continue;

      // A collapsed size container is a TALL, zero-width box. Requiring height
      // skips display:none subtrees and empty nodes (both 0x0), which are
      // zero-width for legitimate reasons.
      var rect = node.getBoundingClientRect();
      if (rect.width >= 4 || rect.height <= 24) continue;

      var cs = window.getComputedStyle(node);
      // `container-type` computes to 'normal' unless the author set it; browsers
      // without container queries report undefined — and cannot have this bug.
      var containerType = cs.containerType;
      if (!containerType || containerType === 'normal') continue;
      // Out-of-flow boxes shrink-to-fit by design; leave them alone.
      if (cs.position === 'absolute' || cs.position === 'fixed') continue;

      collapsed.push(node);
    }

    for (var j = 0; j < collapsed.length; j++) {
      collapsed[j].setAttribute(SIZE_GUARD_ATTR, '1');
      collapsed[j].style.width = '100%';
      collapsed[j].style.minWidth = '0';
    }

    return collapsed.length;
  }

  function guardCollapsedSizeContainers(root) {
    if (!root || !root.querySelectorAll || !window.getComputedStyle) return;
    for (var pass = 0; pass < 3; pass++) {
      if (!collapsedSizeContainerPass(root)) return;
    }
  }

  // ============================================================
  // 14. Empty-column collapse
  //
  // Every renderer that cannot draw its element returns '' — `image` with no
  // `content.src` (the builder shows a dashed "choose an image" placeholder and
  // saves nothing), `youtube`/`video` with no id, `linked-form` with no form,
  // renderUnknown. But wrapColumn()/wrapRow() in the engine run unconditionally
  // and `container` emits its styled div whatever its children rendered to, so
  // the column survives its content and keeps claiming its share of the row:
  // /pages/enzonet-lounge showed an empty dark box where the photo should be,
  // with the text squeezed into the other half.
  //
  // Nothing in the document says "this column is empty" — only the rendered DOM
  // does — so this is a hydrate-time pass, like the size-container guard above,
  // and it is deliberately timid: a wrongly collapsed column is a far worse bug
  // than an empty gap, and this renderer serves ~30 live pages. Three rules do
  // the containment:
  //
  //   a) Only fire when the row has >= 2 columns and EXACTLY ONE of them is
  //      meaningful. With one survivor "collapse" has an unambiguous meaning —
  //      it takes the row. With two survivors of three it would not, and the
  //      authored track placement would start to matter.
  //   b) A `display:none` element counts as MEANINGFUL. That reads backwards
  //      until you look at what these documents do: nos-fans and hospitalites
  //      author real placeholder panels (`.nf-media__ph`, `.hosp-media__panel`)
  //      and hide them from their own CSS, which is authored emptiness — the
  //      author owns that column and may reveal it at another breakpoint. Only
  //      a column the RENDERER emptied (nothing but wrapper divs and the mobile
  //      <style> blocks this file prefixes) is collapsed.
  //   c) Background images, visible borders, thin painted bars (a rule or
  //      divider) and `spacer` elements all count as meaningful. A plain
  //      background-color on a full-size box does not — that is exactly the
  //      enzonet box.
  //
  // Every multi-column row in these documents is authored `display:grid` with an
  // explicit `grid-template-columns` (the stylesheet's flex row is only the
  // unstyled default), and in a grid, hiding a child does NOT free its track —
  // the survivor would just auto-place into the FIRST track and could end up
  // narrower than before. So on grid rows the survivor is additionally spanned
  // across all tracks, and only when the author left it auto-placed.
  // ============================================================

  var EMPTY_COLUMN_ATTR = 'data-neo-empty-column';
  var COLUMN_SPAN_ATTR = 'data-neo-column-span';

  // Never paint anything. <style> is the load-bearing one: every renderer in
  // this file prefixes its markup with a `styles.mobile` <style> block, and its
  // CSS text would otherwise read as "this column has text".
  var NON_VISUAL_TAGS = {
    STYLE: 1, SCRIPT: 1, TEMPLATE: 1, NOSCRIPT: 1, LINK: 1, META: 1
  };

  // Replaced or interactive elements: they show something (or do something)
  // without carrying any text of their own.
  var VISUAL_TAGS = {
    IMG: 1, PICTURE: 1, SVG: 1, VIDEO: 1, AUDIO: 1, IFRAME: 1, CANVAS: 1,
    OBJECT: 1, EMBED: 1, FORM: 1, INPUT: 1, TEXTAREA: 1, SELECT: 1, BUTTON: 1,
    A: 1, HR: 1
  };

  function hasVisibleBorder(cs) {
    var styles = cs.borderTopStyle + ' ' + cs.borderRightStyle + ' ' +
      cs.borderBottomStyle + ' ' + cs.borderLeftStyle;
    if (!/solid|dashed|dotted|double|groove|ridge|inset|outset/.test(styles)) return false;
    return parseFloat(cs.borderTopWidth) > 0 || parseFloat(cs.borderRightWidth) > 0 ||
      parseFloat(cs.borderBottomWidth) > 0 || parseFloat(cs.borderLeftWidth) > 0;
  }

  /** A painted rule/bar: coloured, and thin in one direction. */
  function isPaintedBar(node, cs) {
    var bg = cs.backgroundColor;
    if (!bg || bg === 'transparent' || /rgba\([^)]*,\s*0\s*\)/.test(bg)) return false;
    var rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    return rect.height <= 24 || rect.width <= 24;
  }

  function isMeaningfulNode(node) {
    if (!node) return false;
    if (node.nodeType === 3) return /\S/.test(node.nodeValue || '');
    if (node.nodeType !== 1) return false; // comments (incl. the §10 markers)
    if (NON_VISUAL_TAGS[node.tagName]) return false;
    if (VISUAL_TAGS[node.tagName]) return true;
    // An authored spacer is emptiness on purpose.
    if (node.className && String(node.className).indexOf('content-type-neo-spacer') !== -1) {
      return true;
    }

    var cs = window.getComputedStyle(node);
    // See (b): hidden by the document's own CSS = the author owns this column.
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;
    if (cs.backgroundImage && cs.backgroundImage !== 'none') return true;
    if (hasVisibleBorder(cs)) return true;
    if (isPaintedBar(node, cs)) return true;

    var kids = node.childNodes;
    for (var i = 0; i < kids.length; i++) {
      if (isMeaningfulNode(kids[i])) return true;
    }
    return false;
  }

  /**
   * The column WRAPPER is renderer-emitted, never authored, so it is classified
   * by what it contains — not by itself. That distinction matters because Dawn's
   * base.css hides `div:empty`, so a column the renderer emptied completely is
   * already `display:none` and rule (b) would otherwise read it as authored
   * intent and keep the row as it is. (It still owns its grid track: the
   * survivor auto-places into track 1 and can come out NARROWER than the row.)
   */
  function isColumnEmpty(column) {
    var kids = column.childNodes;
    for (var i = 0; i < kids.length; i++) {
      if (isMeaningfulNode(kids[i])) return false;
    }
    return true;
  }

  function collapseEmptyColumns(root) {
    if (!root || !root.querySelectorAll || !window.getComputedStyle) return;

    var rows = root.querySelectorAll('.content-row');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var columns = [];
      var kids = row.children;
      for (var k = 0; k < kids.length; k++) {
        if (kids[k].classList && kids[k].classList.contains('content-column')) {
          columns.push(kids[k]);
        }
      }
      if (columns.length < 2) continue; // a single-column row has nothing to give

      var empties = [];
      var survivors = [];
      for (var c = 0; c < columns.length; c++) {
        // Already collapsed: classify by the marker, not by measuring a
        // display:none node — that keeps repeat passes idempotent.
        if (columns[c].hasAttribute(EMPTY_COLUMN_ATTR)) empties.push(columns[c]);
        else if (isColumnEmpty(columns[c])) empties.push(columns[c]);
        else survivors.push(columns[c]);
      }
      // Rule (a): one unambiguous survivor, or leave the row alone. An
      // all-empty row also falls through here and is left exactly as it was.
      if (!empties.length || survivors.length !== 1) continue;

      for (var e = 0; e < empties.length; e++) {
        empties[e].setAttribute(EMPTY_COLUMN_ATTR, '1');
        empties[e].style.display = 'none';
      }

      var survivor = survivors[0];
      if (survivor.hasAttribute(COLUMN_SPAN_ATTR)) continue;
      var rowStyle = window.getComputedStyle(row);
      if (rowStyle.display !== 'grid' && rowStyle.display !== 'inline-grid') continue;
      var survivorStyle = window.getComputedStyle(survivor);
      // An explicitly placed column belongs to the author; only stretch the
      // auto-placed default across the tracks the hidden columns left behind.
      if (survivorStyle.gridColumnStart !== 'auto' || survivorStyle.gridColumnEnd !== 'auto') {
        continue;
      }
      survivor.setAttribute(COLUMN_SPAN_ATTR, '1');
      survivor.style.gridColumn = '1 / -1';
    }
  }

  /**
   * Both DOM-measuring passes, in dependency order: collapsing a column changes
   * the widths the size-container guard measures.
   */
  function runLayoutGuards(root) {
    try {
      collapseEmptyColumns(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: empty-column collapse failed', err);
      }
    }
    guardCollapsedSizeContainers(root);
  }

  // ============================================================
  // 15. Theme slots
  //
  // Some page furniture cannot be authored in aiconeo at all: the jersey
  // configurator (sections/jersey-configurator.liquid) resolves products,
  // variants, section blocks and metafields in Liquid, so it exists only as a
  // server-rendered Shopify section. /pages/shop is a Neo document, and the
  // content team still wants to decide WHERE inside that document it sits.
  //
  // The contract is deliberately generic, so other sections can reuse it:
  //
  //   theme side — the section root carries `data-ls-slot-source="<name>"`.
  //                assets/content-builder-neo.css hides it until it is placed,
  //                and that stylesheet only loads on pages that HAVE Neo
  //                content, so an ordinary page renders the section as usual.
  //   neo side   — the author drops a Custom HTML element holding
  //                `<div data-ls-slot="<name>">…</div>` (the class form
  //                `.ls-slot-<name>` is accepted as a second marker) wherever
  //                the section belongs. Its inner markup is a placeholder for
  //                the builder canvas only — it is discarded on the storefront.
  //
  // At hydrate the source node is MOVED into the first matching slot. Moving a
  // server-rendered node instead of re-rendering it in JS is the whole point:
  // the renderer produces HTML strings only, and this markup comes from Liquid.
  // jersey-configurator.js binds its listeners to the elements themselves
  // (`init(root)` per `[data-jc]`), so the move is safe before or after init.
  //
  // Emptying the slot first also clears a STALE copy: in the theme editor a
  // re-rendered section reappears inside its `#shopify-section-*` wrapper as a
  // fresh, unplaced node while the previously moved one still sits in the slot.
  //
  // No slot found → the source is revealed where the template put it, i.e.
  // after the Neo content. A missing or mistyped marker must never make a
  // section vanish, so the same reveal also runs on `load` and on a 6s timer —
  // that covers a failed document fetch or a render that never happened.
  //
  // Called from the hydrate wrapper because that is the one place BOTH render
  // paths meet: repaintInlineData() below wipes #aico-content (undoing any
  // earlier placement), and the >64KB URL fallback in
  // snippets/aico-neo-content.liquid renders much later still.
  // ============================================================

  var SLOT_SOURCE_SELECTOR = '[data-ls-slot-source]';
  var SLOT_PLACED_CLASS = 'is-placed';

  /** Slot names reach a CSS selector — keep them to the safe alphabet. */
  var SLOT_NAME_RE = /^[A-Za-z0-9_-]+$/;

  function findThemeSlot(scope, name) {
    if (!scope || !scope.querySelector) return null;
    return (
      scope.querySelector('[data-ls-slot="' + name + '"]') ||
      scope.querySelector('.ls-slot-' + name)
    );
  }

  /**
   * Move every `[data-ls-slot-source]` into its marker inside `root`.
   * Idempotent: a source already sitting in a filled slot is skipped, so
   * repeated hydrates (inline repaint, URL fallback, editor reloads) are free.
   * Logs ONE aggregated line per call.
   */
  function placeThemeSlotSources(root) {
    var scope = root && root.querySelector ? root : document;
    var sources = document.querySelectorAll(SLOT_SOURCE_SELECTOR);
    if (!sources.length) return;

    var placed = [];
    var unplaced = [];
    // Moves only: a re-hydrate that finds everything already in place must not
    // make consumers re-init for nothing (see the event at the end).
    var moved = 0;

    for (var i = 0; i < sources.length; i++) {
      var source = sources[i];
      // Skip only sources that already sit in a filled slot. A source merely
      // REVEALED by the safety net (load / 6s timer fired before a slow URL-
      // fallback render) is still eligible to be moved into its slot.
      if (source.parentNode && source.parentNode.classList &&
          source.parentNode.classList.contains('is-filled')) continue;

      var name = (source.getAttribute('data-ls-slot-source') || '').trim();
      var slot = SLOT_NAME_RE.test(name) ? findThemeSlot(scope, name) : null;

      if (slot && slot !== source && !slot.contains(source)) {
        slot.textContent = '';
        slot.removeAttribute('style');
        slot.classList.add('is-filled');
        slot.appendChild(source);
        placed.push(name);
        moved += 1;
      } else if (slot) {
        // Already sitting in its slot — nothing to move.
        placed.push(name);
      } else {
        unplaced.push(name || '(unnamed)');
      }

      source.classList.add(SLOT_PLACED_CLASS);
    }

    if (!placed.length && !unplaced.length) return;
    if (window.console && console.info) {
      console.info(
        'content-builder-neo-ext: theme slots — placed: ' +
          (placed.length ? placed.join(', ') : 'none') +
          '; shown in template position: ' +
          (unplaced.length ? unplaced.join(', ') : 'none')
      );
    }

    // 15b. Late placement signal
    //
    // On the inline-metafield path this runs before DOMContentLoaded, so every
    // consumer measures its section in the final position. The >64KB URL
    // fallback in snippets/aico-neo-content.liquid and theme-editor
    // `shopify:section:load` re-renders both move nodes AFTER those scripts
    // initialised — a section that measured geometry (GSAP pin in ls-tv.js,
    // the players slider track) is then wrong until something re-inits it.
    // Announce the move so those sections can recover; `placed` lets a
    // listener ignore moves it does not care about.
    if (!moved) return;
    try {
      document.dispatchEvent(new CustomEvent('ls:theme-slots-placed', {
        detail: { placed: placed, unplaced: unplaced }
      }));
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: theme slot event failed', err);
      }
    }
  }

  /**
   * Safety net: reveal anything still hidden. Runs on `load` and on a timer so
   * a document that never rendered (fetch error, empty metafield) cannot leave
   * a section invisible.
   */
  function revealThemeSlotSources() {
    var pending = document.querySelectorAll(
      SLOT_SOURCE_SELECTOR + ':not(.' + SLOT_PLACED_CLASS + ')'
    );
    for (var i = 0; i < pending.length; i++) {
      pending[i].classList.add(SLOT_PLACED_CLASS);
    }
    clearNeoLoadingState();
  }

  /**
   * `html.ls-neo-loading` is set by snippets/aico-neo-content.liquid at parse
   * time and hides the footer until the Neo body exists (content-builder-neo.css
   * "Loading state"). Cleared after every hydrate and by every safety net.
   */
  function clearNeoLoadingState() {
    document.documentElement.classList.remove('ls-neo-loading');
  }

  window.addEventListener('load', revealThemeSlotSources);
  window.setTimeout(revealThemeSlotSources, 6000);

  // Theme editor: a re-rendered section comes back unplaced inside its
  // `#shopify-section-*` wrapper, so place it again against the mounted page.
  document.addEventListener('shopify:section:load', function () {
    try {
      placeThemeSlotSources(document.getElementById('aico-content') || document);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: theme slot placement failed', err);
      }
    }
  });

  var originalHydrate = Neo.hydrate;
  Neo.hydrate = function (root) {
    originalHydrate(root);
    try {
      hydrateForms(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: form hydration failed', err);
      }
    }
    try {
      runLayoutGuards(root);
      // Web fonts and lazy images can land after hydrate and turn a block that
      // measured empty into a real one; re-check once the page has settled.
      // The data-attribute markers keep both passes idempotent.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', function () {
          try {
            runLayoutGuards(root);
          } catch (e) { /* layout guards are best-effort */ }
        }, { once: true });
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: layout guards failed', err);
      }
    }
    try {
      placeThemeSlotSources(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: theme slot placement failed', err);
      }
    }
    clearNeoLoadingState();
  };

  // ============================================================
  // 7. element.className / element.customId
  //
  // aiconeo stamps both onto every element; the engine ignores both. On
  // /pages/ls-is-more that silently killed the page's entire responsive system:
  // the authored CSS is `.lsim-wide, div:has(> .lsim-wide){…}` plus
  // `.lsim-wide{container-type:inline-size}` and @container queries, so with the
  // class dropped no containment context existed, every @container rule was dead
  // and all the pricing grids collapsed to one column. The seven `customId`s are
  // in-page nav anchors (the CSS even ships `[id]{scroll-margin-top:90px}`).
  //
  // Placement matches ElementRenderer exactly:
  //   className → appended to the element's OWN wrapper node, i.e. the same node
  //               that carries its inline styles (`wrapperClasses` there).
  //   customId  → an EXTRA outer div wrapping the whole element
  //               (`node = <div id={customId}>{node}</div>`).
  // That ordering is load-bearing here: the authored `div:has(> .lsim-wide)`
  // twin selector only matches if `.lsim-wide` is a direct child of its wrapper.
  //
  // Applied by decorating the renderer registry itself, so it covers engine
  // types and ext types identically, on both render paths, exactly once —
  // the engine's renderElement and this file's renderChild both dispatch
  // through Neo._renderers.
  //
  // KNOWN GAP: a SECTION-LEVEL `columns` element is intercepted by the engine's
  // renderSection before dispatch, so it never passes through the registry and
  // would not be decorated. Neither pilot document does that (nested columns,
  // which do go through renderChild, are covered). Left alone rather than
  // reaching into the engine.
  // ============================================================

  /** Author-controlled, emitted as an id attribute — allow [A-Za-z0-9_-] only. */
  function sanitizeCustomId(value) {
    if (value == null) return '';
    return String(value).replace(/[^A-Za-z0-9_-]/g, '');
  }

  /**
   * Append `cls` to the class list of the first element tag in `html`.
   * Returns the html untouched when there is no element root to stamp.
   */
  function injectClass(html, cls) {
    var tag = /^<([a-z][\w-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/i.exec(html);
    if (!tag) return html;

    var attrs = tag[2];
    var existing = /\sclass\s*=\s*"([^"]*)"/i.exec(attrs);
    var newAttrs = existing
      ? attrs.replace(existing[0], ' class="' + existing[1] + ' ' + escapeAttr(cls) + '"')
      : attrs + ' class="' + escapeAttr(cls) + '"';

    return '<' + tag[1] + newAttrs + '>' + html.slice(tag[0].length);
  }

  function decorateElementHtml(el, html) {
    if (!html || !el) return html;

    // Renderers in this file prefix a <style> block for styles.mobile; keep it
    // outside the customId wrapper so that wrapper's first child is the
    // element node itself (the authored `div:has(> .lsim-wide)` depends on it).
    var lead = /^\s*(?:<style>[\s\S]*?<\/style>\s*)*/.exec(html);
    var prefix = lead ? lead[0] : '';
    var body = html.slice(prefix.length);

    var cls = el.className ? String(el.className).trim() : '';
    if (cls) body = injectClass(body, cls);

    var customId = sanitizeCustomId(el.customId);
    if (customId) body = '<div id="' + escapeAttr(customId) + '">' + body + '</div>';

    return prefix + body;
  }

  (function decorateRegistry() {
    var registry = Neo._renderers;
    Object.keys(registry).forEach(function (type) {
      var original = registry[type];
      if (typeof original !== 'function' || original.__neoDecorated) return;
      var decorated = function (el) {
        return decorateElementHtml(el, original(el));
      };
      decorated.__neoDecorated = true;
      registry[type] = decorated;
    });
  })();

  // ============================================================
  // Repaint the inline-data path
  //
  // The engine's own bootstrap runs the moment content-builder-neo.js executes:
  // a `defer` script runs with document.readyState === 'interactive', so its
  // `readyState === 'loading'` guard is false and it calls boot() immediately —
  // BEFORE this file has registered anything. An inline-data page would
  // therefore paint without container/linked-form. Repaint once here, now that
  // the registry is complete. Both scripts run back-to-back in the same
  // deferred-script phase, so this is not a visible reflow.
  //
  // The >64KB URL-fallback path renders later (snippets/aico-neo-content.liquid,
  // on DOMContentLoaded) and already picks up the full registry.
  // ============================================================

  (function repaintInlineData() {
    var mount = document.getElementById('aico-content');
    var dataEl = document.getElementById('aico-page-data');
    if (!mount || !dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent || dataEl.innerText || '{}');
    } catch (e) {
      return;
    }
    if (!Neo.hasContent(data)) return;

    mount.innerHTML = Neo.render(data);
    Neo.hydrate(mount);
  })();

  // ============================================================
  // 19. Locale resolution for the URL-fallback path
  //
  // The <64KB metafield keys (`custom_fields.content_builder_json_<lang>`) are
  // resolved to one language by the aico backend. The >64KB fallback fetches
  // the RAW Neo file instead, which still carries the builder's locale
  // structure:
  //
  //   baseLocale:      'fr_CH'                       (language of the base content)
  //   localeOverrides: { en: { elements: { <id>: { content: {…only differing fields} } },
  //                            seo: {…partial} },
  //                      en_GB: {…} }               (sparse, per locale)
  //
  // The engine only reads `sections`, so without this pass every visitor got
  // the base language regardless of the FR/EN switcher. `resolveLocale` is a
  // line-for-line port of aico's Modules/Storefront/Support/NeoLocaleResolver:
  //
  //   1. requested language IS the base language → nothing to apply;
  //   2. else apply the same-language entry (`en`) first and the exact entry
  //      (`en_GB`) on top, field-level `{…base, …override}` per element id,
  //      recursing into `children`;
  //   3. no entry for the requested language → the shop default locale's
  //      entries (unless the base already speaks it);
  //   4. none → base content.
  //
  // Then `localeOverrides` / `baseLocale` are stripped so the renderer stays
  // locale-unaware. A document WITHOUT the structure passes through untouched
  // (every page authored before translations existed). Mutates and returns the
  // given document — the fetched payload is ours.
  // ============================================================

  function localeNormalize(locale) {
    return String(locale == null ? '' : locale).trim().replace(/-/g, '_');
  }

  function localeLanguage(locale) {
    var normalized = localeNormalize(locale);
    var separator = normalized.indexOf('_');
    return (separator === -1 ? normalized : normalized.slice(0, separator)).toLowerCase();
  }

  function localeSameLanguage(first, second) {
    var language = localeLanguage(first);
    return language !== '' && language === localeLanguage(second);
  }

  /** Entries serving `locale`, weakest first: same-language keys, then the exact key last so it wins the merge. */
  function localeMatchKeys(availableKeys, locale) {
    var exact = null;
    var sameLanguage = [];
    var wanted = localeNormalize(locale).toLowerCase();
    for (var i = 0; i < availableKeys.length; i++) {
      var key = availableKeys[i];
      if (localeNormalize(key).toLowerCase() === wanted) {
        exact = key;
      } else if (localeSameLanguage(key, locale)) {
        sameLanguage.push(key);
      }
    }
    return exact === null ? sameLanguage : sameLanguage.concat([exact]);
  }

  function localePickOverrideKeys(availableKeys, locale, defaultLocale, baseLocale) {
    if (!availableKeys.length || !locale) return [];

    // The base content already speaks the requested language.
    if (baseLocale && localeSameLanguage(locale, baseLocale)) return [];

    var matches = localeMatchKeys(availableKeys, locale);
    if (matches.length) return matches;

    if (defaultLocale && !(baseLocale && localeSameLanguage(defaultLocale, baseLocale))) {
      matches = localeMatchKeys(availableKeys, defaultLocale);
      if (matches.length) return matches;
    }

    return [];
  }

  function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function localeApplyElementOverrides(elements, overridesByElementId) {
    for (var i = 0; i < elements.length; i++) {
      var element = elements[i];
      if (!isPlainObject(element)) continue;

      var elementId = element.id;
      var override = (typeof elementId === 'string' || typeof elementId === 'number')
        ? overridesByElementId[elementId]
        : null;
      if (isPlainObject(override) && isPlainObject(override.content)) {
        var merged = {};
        var base = isPlainObject(element.content) ? element.content : {};
        for (var baseKey in base) if (Object.prototype.hasOwnProperty.call(base, baseKey)) merged[baseKey] = base[baseKey];
        for (var overrideKey in override.content) if (Object.prototype.hasOwnProperty.call(override.content, overrideKey)) merged[overrideKey] = override.content[overrideKey];
        element.content = merged;
      }

      if (Array.isArray(element.children)) {
        localeApplyElementOverrides(element.children, overridesByElementId);
      }
    }
  }

  function hasLocaleStructure(document) {
    if (!isPlainObject(document)) return false;
    if (typeof document.baseLocale === 'string' && document.baseLocale.trim() !== '') return true;
    var overrides = document.localeOverrides;
    return isPlainObject(overrides) && Object.keys(overrides).length > 0;
  }

  function resolveLocale(document, locale, defaultLocale) {
    if (!isPlainObject(document)) return document;

    var overrides = isPlainObject(document.localeOverrides) ? document.localeOverrides : {};
    var baseLocale = typeof document.baseLocale === 'string' ? document.baseLocale : null;
    var overrideKeys = localePickOverrideKeys(Object.keys(overrides), locale, defaultLocale, baseLocale);

    for (var i = 0; i < overrideKeys.length; i++) {
      var override = overrides[overrideKeys[i]];
      if (!isPlainObject(override)) continue;

      var elementOverrides = isPlainObject(override.elements) ? override.elements : null;
      if (elementOverrides && Array.isArray(document.sections)) {
        for (var s = 0; s < document.sections.length; s++) {
          var section = document.sections[s];
          if (isPlainObject(section) && Array.isArray(section.elements)) {
            localeApplyElementOverrides(section.elements, elementOverrides);
          }
        }
      }

      if (isPlainObject(override.seo) && Object.keys(override.seo).length) {
        var seo = {};
        var baseSeo = isPlainObject(document.seo) ? document.seo : {};
        for (var seoKey in baseSeo) if (Object.prototype.hasOwnProperty.call(baseSeo, seoKey)) seo[seoKey] = baseSeo[seoKey];
        for (var overrideSeoKey in override.seo) if (Object.prototype.hasOwnProperty.call(override.seo, overrideSeoKey)) seo[overrideSeoKey] = override.seo[overrideSeoKey];
        document.seo = seo;
      }
    }

    delete document.localeOverrides;
    delete document.baseLocale;

    return document;
  }

  Neo.hasLocaleStructure = hasLocaleStructure;
  Neo.resolveLocale = resolveLocale;

  // ============================================================
  // 18. Ready signal for the URL-fallback path
  //
  // snippets/aico-neo-content.liquid fetches the >64KB document during parse
  // and must not paint before this registry is complete. It used to wait for
  // DOMContentLoaded, which on a heavy page fires long after this deferred
  // script ran (every other deferred script sits in between). Announce
  // readiness right here instead — the DOM is fully parsed by the time a
  // deferred script executes, so painting now is safe.
  // ============================================================

  window.aicoNeoExtReady = true;
  try {
    document.dispatchEvent(new CustomEvent('aico-neo:ext-ready'));
  } catch (err) { /* CustomEvent unsupported: the DOMContentLoaded fallback still fires */ }

})(window, document);
